import {$controller} from "rapid/index";

import {forEach, filter, cloneDeep, isEqual, isArray} from "lodash";

import {FILE_SIZES} from "../../../constants/file-sizes.constant";
import {COLOR_PICKER_OPTIONS} from "../../../constants/color-picker.constant";
// Print formats A1–A6, 10×15, 15×20, 2:3–4:3; getPixelsForFormat, options for md-select
import {getPixelsForFormat, getFormatShortLabel, PRINT_FORMAT_OPTION_GROUPS, PRINT_DPI_OPTIONS} from "../../../constants/print-formats.constant.js";
import {
    getDefaultIndividualText,
    normalizeIndividualText,
    clampOutputPosition,
    clampScreenArea,
    scaleOutputFont,
    sanitizeIndividualText,
    INDIVIDUAL_TEXT_INPUT_TYPES,
    INDIVIDUAL_TEXT_ALIGN_OPTIONS,
    INDIVIDUAL_TEXT_VERTICAL_ALIGN_OPTIONS
} from "../../../constants/individual-text.constant";

const dependencies = ["$Configuration", "$companies", "$service"];
const REPLICATE_PROMPT_MODELS = {
    pro: "google/nano-banana-pro",
    normal: "google/nano-banana-2",
    faster: "black-forest-labs/flux-kontext-pro"
};
const REPLICATE_PROMPT_DEFAULT_QUALITY = "normal";
const VIDEO_CHROMAKEY_FEATURE = "video_chromakey";

class ConfigurationUpdateController extends $controller {

    constructor($injector, $stateParams, $scope, $photoTransform, $ToViewValue, $ToBoothValue, $EmailTemplates, $DetectChanges, $auth, $ToastService, WizardHandler, $upload) {
        "ngInject";
        super($injector, dependencies);
        this.$scope = $scope;
        this.$stateParams = $stateParams;
        this.isNew = this.$stateParams.isNew;
        this.photoTransform = $photoTransform;
        this.toViewValue = $ToViewValue;
        this.toBoothValue = $ToBoothValue;
        this.emailTemplates = $EmailTemplates;
        this.detectChanges = $DetectChanges;
        this.$auth = $auth;
        this.Math = Math;
        this.pickerOptions = Object.assign({}, COLOR_PICKER_OPTIONS);
        this.toastService = $ToastService;
        this.loading = true;
        this.wizardHandler = WizardHandler;
        this.formsOneAnswer = false;
        this.loadingTost = {
            template: "<md-toast>Saving changes ...</md-toast>",
            hideDelay: 0,
            position: "top right",
            toastClass: "loading"
        };
        this.$http = $injector.get("$http");
        this.showApiKeyForm = false;
        this.showReplicateApiKeyForm = false;
        this.showRemakerApiKeyForm = false;
        this.removeBg = {};
        this.replicate = {};
        this.remaker = {};
        this.unscreen = {};
        this.wandershareApi = {};
        this.displayFaceSwapConnectBlock = false;
        this.removeBgIsConnectedState = false;
        this.replicateIsConnectedState = false;
        this.remakerIsConnectedState = false;
        this.unscreenIsConnectedState = false;
        this.wandershareApiIsConnectedState = false;
        this.faceSwapApiIsConnectedState = false;
        this.faceSwapMultiOptionToggle = false;
        this.$upload = $upload;
        this.formErrorFields = [];
        this.sceneActiveFeatures = [];
        this.gdpr = {
            showGdpr: false,
            gdprFile: null,
            fileId: null,
            gdprCustomButtonsEnabled: false,
            gdprAcceptButtonImage: null,
            gdprDeclineButtonImage: null
        };

        // Options for md-select "Layout aspect ratio" and "Print quality / DPI"
        this.printFormatOptionGroups = PRINT_FORMAT_OPTION_GROUPS;
        this.printDpiOptions = PRINT_DPI_OPTIONS;
        this.individualTextInputTypes = INDIVIDUAL_TEXT_INPUT_TYPES;
        this.individualTextAlignOptions = INDIVIDUAL_TEXT_ALIGN_OPTIONS;
        this.individualTextVerticalAlignOptions = INDIVIDUAL_TEXT_VERTICAL_ALIGN_OPTIONS;
        // Collapsed setting blocks of the Individual Text tab, by field id (not saved)
        this.individualTextCollapsed = {};
        this.watchIndividualTextLayout();

        $scope.$on("wizard:stepChanged", (event, args) => {
            this.currentStep = args.step.wzTitle;
            if (this.canExitSharingScreen()
                && this.canExitScreen("singlePhoto", "photoBooth")
                && this.canExitScreen("gif", "gifBooth")
                && this.canExitScreen("boomerang", "boomerangBooth")
                && this.canExitStartScreen()
                && this.canExitCustomButtons()
                && this.canExitScenes()
                && this.canExitFaceSwapScenes()
                && this.canExitFaceSwapOverlays()
                && this.canExitForms()) {
                this.checkDesignForAllModes();
                this.changeScenes();
                this.saveConfiguration(false);
            } else if (!this.loading) {
                this.saveConfiguration(false);
                this.canExitWizard();
            }
        });
        $scope.$on("wizard:stepChangeFailed", () => {
            if (!this.loading) {
                if (!this.canExitSharingScreen() && this.currentStep === "Sharing Settings" && this.formSharing && this.formSharing.$valid) {
                    this.showErrorDialog("Error", "<p>You must allow email, QR-code or sms sharing if</p> <p>this option is provided in photo modes</p>");
                } else if (!this.canExitScenes()) {
                    this.showErrorDialog("Error", "<p>Please, enable at least one scene and </p> <p>fill out all fields  or disable this option</p>");
                } else {
                    this.showErrorDialog("Error", "<p>Please, fill out the missing fields</p>");
                }
            }
        });
        this.whenOfflineForm = [
            {
                "value": "beforePhoto",
                "label": "before taking photo"
            },
            {
                "value": "afterPhoto",
                "label": "after taking photo"
            }
        ];
        this.fileSizes = FILE_SIZES;

        // temporary set canvas size for printout
        this.printCanvasWidth = 1240;
        this.printCanvasHeight = 1844;
        this.animatedIcons = false;
        this.viewValue = this.toViewValue.getDefaultViewValue();
        this.storedConfig = angular.copy(this.viewValue);

        let config;
        if ($stateParams.cid && $stateParams.cid !== "create") {
            config = this.$Configuration.get({id: $stateParams.cid});
        } else {
            config = this.$Configuration.get({url: this.$companies.getCached().default_configuration});
        }

        config && config.$promise
            .then((config) => {
                this.config = config;
                delete config["us_active"];
                delete config["us_api_key"];
                this.json = config.json;
                if (!this.json.admin.videoBooth) {
                    Object.assign(this.json.admin, {
                        videoBooth: {
                            enabled: false,
                            allowSharing: true,
                            enableChromakey: false,
                            enableSoundRecording: false,
                            countdownTime: 5,
                            playbackSpeed: 1,
                            captureTime: 1.5,
                            resolutionOptions: "hd",
                            sliderValue: 300
                        }
                    });
                }
                this.ensureVideoBoothFields(this.json.admin.videoBooth);
                if (this.json.admin.removeBg == undefined) {
                    this.json.admin["removeBg"] = {active: false};
                }
                if (this.json.admin.unscreen == undefined) {
                    this.json.admin["unscreen"] = {us_active: false};
                }
                if (this.json.admin.ai == undefined) {
                    this.json.admin["ai"] = {wandershareApi: {active: false, aging_active: false}};
                }
                if (this.json.admin.ai.replicate == undefined) {
                    this.json.admin.ai["replicate"] = {
                        replicate_remove_bg_active: false,
                        replicate_api_key: "",
                        replicate_prompting_active: false,
                        replicate_prompt_quality: REPLICATE_PROMPT_DEFAULT_QUALITY,
                        replicate_model: REPLICATE_PROMPT_MODELS[REPLICATE_PROMPT_DEFAULT_QUALITY]
                    };
                } else {
                    this.normalizeReplicatePromptingState(this.json.admin.ai.replicate);
                }
                if (this.json.admin.ai.remaker == undefined) {
                    this.json.admin.ai["remaker"] = {remaker_remove_bg_active: false, remaker_api_key: ""};
                }
                if (this.json.admin.ai.wandershareApi == undefined) {
                    this.json.admin["ai.wandershareApi"] = {active: false, aging_active: false};
                } else {
                    this.getWondershareApiData();
                }
                if (this.json.admin.ai.faceSwap == undefined) {
                    this.json.admin["ai.faceSwap"] = {
                        active: false,
                        face_swap_active: false,
                        face_swap_api_key: "",
                        display_scene_button_title: false,
                        display_scene_names: false,
                    };
                }
                if (this.json.admin.gdpr == undefined) {
                    Object.assign(this.json.admin, {
                        gdpr: {
                            showGdpr: false,
                            gdprFile: null,
                            fileId: null,
                            gdprCustomButtonsEnabled: false,
                            gdprAcceptButtonImage: null,
                            gdprDeclineButtonImage: null,
                        }
                    });
                }
                if (this.json.admin.ai.customLogo == undefined) {
                    Object.assign(this.json.admin.ai, {
                        customLogo: {
                            active: false,
                            customLogoFile: null,
                            fileId: null,
                        }
                    });
                }
                if (this.json.admin.liveView.customLogo === undefined) {
                    Object.assign(this.json.admin.liveView, {
                        customLogo: {
                            enabled: true,
                            file: null,
                            hideDefaultLogo: false,
                            background: {
                                enabled: true,
                                mode: "color",
                                color: "white",
                                image: null
                            },
                            screenText: {
                                enabled: false,
                                title: "Your image is being created",
                                subText: "It may take a few seconds text",
                                color: "black"
                            }
                        },
                    });
                }
                if (
                    this.json.admin.liveView.customLogo &&
                    this.json.admin.liveView.customLogo.hideDefaultLogo === undefined
                ) {
                    this.json.admin.liveView.customLogo.hideDefaultLogo = false;
                }
                this.ensureCustomSceneScreenConfig();
                if (!this.json.admin.hasOwnProperty("filtersNamesShow")) {
                    Object.assign(this.json.admin, {filtersNamesShow: false});
                }
                this.ensureIndividualText();
                this.ensureOfflineQrCode();
                this.deleteUpdatedProperties();
                this.copiedConfig = cloneDeep(this.config);
                if (this.json.client && this.json.client.emailTextNoHtml) {
                    this.json.client.emailTextNoHtml = this.emailTemplates.removeLineBreaks(this.json.client.emailTextNoHtml);
                }
                this.transformConfigToViewValue();
                if (this.json.admin.liveView && this.json.admin.liveView.customButtons === undefined) {
                    Object.assign(this.json.admin.liveView, {
                        customButtons: {
                            active: false,
                            generalUiButtonsSettings: {
                                back: {width: 4.5, height: 4.5, x: 8, y: 7},
                                sharingBackBtn: {width: 3.5, height: 3.5, x: 8, y: 7},
                                capture: {width: 5.5, height: 5.5, x: 92, y: 50},
                                proceed: {width: 5.5, height: 5.5, x: 92, y: 50},
                                email: {width: 5.5, height: 5.5, x: 16.5, y: 88},
                                sms: {width: 3.5, height: 3.5, x: 35, y: 88},
                                print: {width: 3.5, height: 3.5, x: 45, y: 88},
                                done: {width: 6, height: 6, x: 89, y: 92.5},
                                qrCode: {width: 7, height: 7, x: 50, y: 11,text:"Scan me to download",textSize: 12,textColor: "black"},
                                close: {width: 3.5, height: 3.5, x: 5, y: 28},
                                next: {width: 5, height: 5, x: 93, y: 50},
                                closeError: {width: 5, height: 5, x: 93, y: 50},
                            },
                            captureModeButtonsSettings: {
                                single: {width: 3.5, height: 3.5, x: 8, y: 44},
                                gif: {width: 3.5, height: 3.5, x: 8, y: 49},
                                boomerang: {width: 3.5, height: 3.5, x: 8, y: 55},
                                scene: {scenePopupColor: "#FFF", width: 36, height: 48, x: 50, y: 57},
                                filter: {width: 5, height: 5, x: 8, y: 38},
                            }
                        },
                    });
                }
                this.ensureGeneralUiButtonsSettings();
                if (this.json.admin.liveView && this.json.admin.liveView.hideOutputPictureVideo === undefined) {
                    this.json.admin.liveView.hideOutputPictureVideo = false;
                }
                if (this.json.admin.liveView && this.json.admin.liveView.hideSharingOptions === undefined) {
                    this.json.admin.liveView.hideSharingOptions = false;
                }
                this.json && this.json.admin.offlineForms && !this.json.admin.offlineForms.when && (this.json.admin.offlineForms.when = "afterPhoto");
                if (this.config.json.client && !this.config.json.client.advancedHtmlInput && this.config.json.client.advancedHtmlInput !== false) {
                    this.config.json.client.advancedHtmlInput = true;
                }
                //use this for fix 'this.json.admin.offlineForms.when' on old configuration where selected value 'onStart'
                if (this.json && this.json.admin.offlineForms && this.json.admin.offlineForms.when === "onStart") {
                    this.json.admin.offlineForms.when = "beforePhoto";
                }
                this.checkBackgroundResolution();
                this.getRemoveBgData();
                this.getReplicateData();
                this.getReplicatePromptingData();
                this.getRemakerData();
                this.getUnscreenData();
                //this.getWondershareApiData();
                this.getFaceSwapApiData();

                this.loading = false;
                this.updateCaptureTime();
                this.ensureVideoBoothFields(this.json.admin.videoBooth);
                this.rebuildSceneActiveFeatures();
            }).catch((e) => {
                console.error(e);
                this.$state.go("configurations");
            });
    }

    canExitWizard() {
        if (!this.canExitStartScreen()) {
            this.wizardHandler.wizard().goTo("Start Screen");
        } else if (!this.canExitScreen("singlePhoto", "photoBooth")) {
            this.wizardHandler.wizard().goTo("Single Photo");
        } else if (!this.canExitScreen("gif", "gifBooth")) {
            this.wizardHandler.wizard().goTo("GIF");
        } else if (!this.canExitScreen("boomerang", "boomerangBooth")) {
            this.wizardHandler.wizard().goTo("Boomerang");
        } else if (!this.canExitSharingScreen()) {
            this.wizardHandler.wizard().goTo("Sharing Settings");
        } else if (!this.canExitScenes()) {
            this.wizardHandler.wizard().goTo("Scenes");
        } else if (!this.canExitForms() && this.wizardHandler.wizard().currentStepTitle() !== "Forms") {
            this.showErrorDialog("Error", "Enter all required inputs");
            this.wizardHandler.wizard().goTo("Forms");
        }
    }

    onAreaChange() {
        this.json.admin.startScreenAreas.random = Math.random();
        this.checkBackgroundResolution();
    }


    $onInit() {
        this.canExitSinglePhoto = () => {
            return true;
        };
        this.changeSinglePhotoLayoutRatio("singlePhoto");
        this.changeSinglePhotoLayoutRatio("gif");
        this.changeSinglePhotoLayoutRatio("boomerang");
        if (this.isNew) {
            this.setPhotosForPrint("singlePhoto");
            this.setPhotosForPrint("gif");
            this.setPhotosForPrint("boomerang");
        }
        this.activatePhotoStrips("gif");
        this.activatePhotoStrips("boomerang");
    }

    transformConfigToViewValue() {
        this.animatedIcons = this.json.admin.startScreenDefaultIcons;
        let mainConfig = cloneDeep(this.json.admin);

        // Get single photos values
        if (mainConfig.photoBooth.sharingFile || mainConfig.photoBooth.printFile) {
            this.viewValue.singlePhoto.photoCaptureRatio = this.toViewValue.getPhotoCaptureRatio("photoBooth", mainConfig);
            if (this.viewValue.singlePhoto.photoCaptureRatio === "custom") {
                this.viewValue.singlePhoto.customPhotoWidth = mainConfig.photoBooth.aspectRatio.width;
                this.viewValue.singlePhoto.customPhotoHeight = mainConfig.photoBooth.aspectRatio.height;
            }
            this.viewValue.singlePhoto.layoutAspectRatio = this.toViewValue.getPrintLayoutRatio("photoBooth", mainConfig);
            this.viewValue.singlePhoto.printDpi = this.toViewValue.getPrintDpi("photoBooth", mainConfig);
            // Pixels from format map (3mm bleed); replaces hardcoded 1240×1844, 2520×1890, etc.
            const pSingle = getPixelsForFormat(this.viewValue.singlePhoto.layoutAspectRatio, this.viewValue.singlePhoto.printDpi);
            this.viewValue.singlePhoto.printCanvasWidth = pSingle.width;
            this.viewValue.singlePhoto.printCanvasHeight = pSingle.height;
            this.viewValue.singlePhoto.strip = mainConfig.photoBooth.printFile.activePhotoStrip;
            this.viewValue.singlePhoto.photosArr = this.toViewValue.getArrayOfPrintingPhotos("photoBooth", mainConfig);
            this.viewValue.singlePhoto.strippedPhotosArr = this.toViewValue.getStrippedPhotos("photoBooth", mainConfig);
            this.viewValue.singlePhoto.digitalFormat.layoutAspectRatio = this.toViewValue.getLayoutCaptureRatio("photoBooth", mainConfig);
            this.viewValue.singlePhoto.digitalFormat.photos = this.toViewValue.getSharingPhoto("photoBooth", mainConfig);
            if (this.viewValue.singlePhoto.digitalFormat.layoutAspectRatio === "custom") {
                if (this.json.admin.ai.faceSwap.active) {
                    // this.viewValue.singlePhoto.digitalFormat.customPhotoWidth = this.toViewValue.detectAspectRatioObject(this.json.admin.general.width,this.json.admin.general.height).w;
                    // this.viewValue.singlePhoto.digitalFormat.customPhotoHeight = this.toViewValue.detectAspectRatioObject(this.json.admin.general.width,this.json.admin.general.height).h;
                    this.viewValue.singlePhoto.digitalFormat.customPhotoWidth = mainConfig.photoBooth.sharingFile.layoutAspectRatio.width;
                    this.viewValue.singlePhoto.digitalFormat.customPhotoHeight = mainConfig.photoBooth.sharingFile.layoutAspectRatio.height;
                } else {
                    this.viewValue.singlePhoto.digitalFormat.customPhotoWidth = mainConfig.photoBooth.sharingFile.layoutAspectRatio.width;
                    this.viewValue.singlePhoto.digitalFormat.customPhotoHeight = mainConfig.photoBooth.sharingFile.layoutAspectRatio.height;
                }
            }
            this.viewValue.singlePhoto.digitalFormat.layoutWidth = mainConfig.photoBooth.sharingFile.finalSize.width;
            this.viewValue.singlePhoto.digitalFormat.layoutHeight = mainConfig.photoBooth.sharingFile.finalSize.height;
        } else {
            this.viewValue.singlePhoto = cloneDeep(this.storedConfig.singlePhoto);
            this.changeSinglePhotoLayoutRatio("singlePhoto");
            this.setPhotosForPrint("singlePhoto");
        }

        // Get gif values
        if (mainConfig.gifBooth.sharingFile || mainConfig.gifBooth.printFile) {
            this.viewValue.gif.photoCaptureRatio = this.toViewValue.getPhotoCaptureRatio("gifBooth", mainConfig);
            if (this.viewValue.gif.photoCaptureRatio === "custom") {
                this.viewValue.gif.customPhotoWidth = mainConfig.gifBooth.aspectRatio.width;
                this.viewValue.gif.customPhotoHeight = mainConfig.gifBooth.aspectRatio.height;
            }
            this.viewValue.gif.layoutAspectRatio = this.toViewValue.getPrintLayoutRatio("gifBooth", mainConfig);
            this.viewValue.gif.printDpi = this.toViewValue.getPrintDpi("gifBooth", mainConfig);
            // Pixels from format map
            const pGif = getPixelsForFormat(this.viewValue.gif.layoutAspectRatio, this.viewValue.gif.printDpi);
            this.viewValue.gif.printCanvasWidth = pGif.width;
            this.viewValue.gif.printCanvasHeight = pGif.height;
            this.viewValue.gif.strip = mainConfig.gifBooth.printFile.activePhotoStrip;
            this.viewValue.gif.photosArr = this.toViewValue.getArrayOfPrintingPhotos("gifBooth", mainConfig);
            this.viewValue.gif.strippedPhotosArr = this.toViewValue.getStrippedPhotos("gifBooth", mainConfig);
            this.viewValue.gif.digitalFormat.layoutAspectRatio = this.toViewValue.getLayoutCaptureRatio("gifBooth", mainConfig);
            this.viewValue.gif.digitalFormat.photos = this.toViewValue.getSharingPhoto("gifBooth", mainConfig);
            if (this.viewValue.gif.digitalFormat.layoutAspectRatio === "custom") {
                if (this.json.admin.ai.faceSwap.active) {
                    // this.viewValue.gif.digitalFormat.customPhotoWidth = this.toViewValue.detectAspectRatioObject(this.json.admin.general.width,this.json.admin.general.height).w;
                    // this.viewValue.gif.digitalFormat.customPhotoHeight = this.toViewValue.detectAspectRatioObject(this.json.admin.general.width,this.json.admin.general.height).h;
                    this.viewValue.gif.digitalFormat.customPhotoWidth = mainConfig.gifBooth.sharingFile.layoutAspectRatio.width;
                    this.viewValue.gif.digitalFormat.customPhotoHeight = mainConfig.gifBooth.sharingFile.layoutAspectRatio.height;
                } else {
                    this.viewValue.gif.digitalFormat.customPhotoWidth = mainConfig.gifBooth.sharingFile.layoutAspectRatio.width;
                    this.viewValue.gif.digitalFormat.customPhotoHeight = mainConfig.gifBooth.sharingFile.layoutAspectRatio.height;
                }
            }
            this.viewValue.gif.digitalFormat.layoutWidth = mainConfig.gifBooth.sharingFile.finalSize.width;
            this.viewValue.gif.digitalFormat.layoutHeight = mainConfig.gifBooth.sharingFile.finalSize.height;
        } else {
            this.viewValue.gif = cloneDeep(this.storedConfig.gif);
            this.changeSinglePhotoLayoutRatio("gif");
            this.setPhotosForPrint("gif");
            this.activatePhotoStrips("gif");
        }

        // get boomerang values
        if (mainConfig.boomerangBooth.sharingFile || mainConfig.boomerangBooth.printFile) {
            this.viewValue.boomerang.photoCaptureRatio = this.toViewValue.getPhotoCaptureRatio("boomerangBooth", mainConfig);
            if (this.viewValue.boomerang.photoCaptureRatio === "custom") {
                this.viewValue.boomerang.customPhotoWidth = mainConfig.boomerangBooth.aspectRatio.width;
                this.viewValue.boomerang.customPhotoHeight = mainConfig.boomerangBooth.aspectRatio.height;
            }
            this.viewValue.boomerang.layoutAspectRatio = this.toViewValue.getPrintLayoutRatio("boomerangBooth", mainConfig);
            this.viewValue.boomerang.printDpi = this.toViewValue.getPrintDpi("boomerangBooth", mainConfig);
            // Pixels from format map
            const pBoom = getPixelsForFormat(this.viewValue.boomerang.layoutAspectRatio, this.viewValue.boomerang.printDpi);
            this.viewValue.boomerang.printCanvasWidth = pBoom.width;
            this.viewValue.boomerang.printCanvasHeight = pBoom.height;
            this.viewValue.boomerang.strip = mainConfig.boomerangBooth.printFile.activePhotoStrip;
            this.viewValue.boomerang.photosArr = this.toViewValue.getArrayOfPrintingPhotos("boomerangBooth", mainConfig);
            this.viewValue.boomerang.strippedPhotosArr = this.toViewValue.getStrippedPhotos("boomerangBooth", mainConfig);
            this.viewValue.boomerang.digitalFormat.layoutAspectRatio = this.toViewValue.getLayoutCaptureRatio("boomerangBooth", mainConfig);
            this.viewValue.boomerang.digitalFormat.photos = this.toViewValue.getSharingPhoto("boomerangBooth", mainConfig);
            if (this.viewValue.boomerang.digitalFormat.layoutAspectRatio === "custom") {
                if (this.json.admin.ai.faceSwap.active) {
                    // this.viewValue.boomerang.digitalFormat.customPhotoWidth = this.toViewValue.detectAspectRatioObject(this.json.admin.general.width,this.json.admin.general.height).w;
                    // this.viewValue.boomerang.digitalFormat.customPhotoHeight = this.toViewValue.detectAspectRatioObject(this.json.admin.general.width,this.json.admin.general.height).h;
                    this.viewValue.boomerang.digitalFormat.customPhotoWidth = mainConfig.boomerangBooth.sharingFile.layoutAspectRatio.width;
                    this.viewValue.boomerang.digitalFormat.customPhotoHeight = mainConfig.boomerangBooth.sharingFile.layoutAspectRatio.height;
                } else {
                    this.viewValue.boomerang.digitalFormat.customPhotoWidth = mainConfig.boomerangBooth.sharingFile.layoutAspectRatio.width;
                    this.viewValue.boomerang.digitalFormat.customPhotoHeight = mainConfig.boomerangBooth.sharingFile.layoutAspectRatio.height;
                }
            }
            this.viewValue.boomerang.digitalFormat.layoutWidth = mainConfig.boomerangBooth.sharingFile.finalSize.width;
            this.viewValue.boomerang.digitalFormat.layoutHeight = mainConfig.boomerangBooth.sharingFile.finalSize.height;
        } else {
            this.viewValue.boomerang = cloneDeep(this.storedConfig.boomerang);
            this.changeSinglePhotoLayoutRatio("boomerang");
            this.setPhotosForPrint("boomerang");
            this.activatePhotoStrips("boomerang");
        }

        // get video values
        if (!mainConfig.videoBooth) {
            Object.assign(mainConfig, {
                videoBooth: {
                    enabled: true,
                    allowSharing: true,
                    enableChromakey: false,
                    enableSoundRecording: false,
                    countdownTime: 3,
                    playbackSpeed: 1,
                    captureTime: 1.5,
                    resolutionOptions: "hd",
                    sliderValue: 300
                }
            });
        }
        this.ensureVideoBoothFields(mainConfig.videoBooth);

        this.formsOneAnswer = !this.json.admin.offlineForms.isMultipleAnswers;

        this.json.admin.startScreenAreas = this.toViewValue.getStartScreenValues(!this.json.admin.startScreenAreas ? this.toViewValue.getDefaultStartScreenAreas() : this.json.admin.startScreenAreas);

        if (this.json && this.json.admin && this.json.admin.liveView && !this.json.admin.liveView.hasOwnProperty("customButtons")) {
            Object.assign(this.json.admin.liveView, {
                customButtons: {
                    active: false,
                    generalUiButtonsSettings: {
                        back: {width: 4.5, height: 4.5, x: 8, y: 7},
                        sharingBackBtn: {width: 3.5, height: 3.5, x: 8, y: 7},
                        capture: {width: 5.5, height: 5.5, x: 92, y: 50},
                        proceed: {width: 5.5, height: 5.5, x: 92, y: 50},
                        email: {width: 5.5, height: 5.5, x: 16.5, y: 88},
                        sms: {width: 3.5, height: 3.5, x: 35, y: 88},
                        print: {width: 3.5, height: 3.5, x: 45, y: 88},
                        done: {width: 6, height: 6, x: 89, y: 92.5},
                        qrCode: {width: 7, height: 7, x: 50, y: 11,text:"Scan me to download",textSize: 12,textColor: "black"},
                        close: {width: 3.5, height: 3.5, x: 5, y: 28},
                        next: {width: 5, height: 5, x: 93, y: 50},
                        closeError: {width: 5, height: 5, x: 93, y: 50},
                    },
                    captureModeButtonsSettings: {
                        single: {width: 3.5, height: 3.5, x: 8, y: 44},
                        gif: {width: 3.5, height: 3.5, x: 8, y: 49},
                        boomerang: {width: 3.5, height: 3.5, x: 8, y: 55},
                        scene: {scenePopupColor: "#FFF", width: 36, height: 48, x: 50, y: 57},
                        filter: {width: 5, height: 5, x: 8, y: 38},
                    }
                }
            });
        }
        this.ensureCustomSceneScreenConfig();
        this.ensureGeneralUiButtonsSettings();

        this.json.admin.photoBooth.playbackSpeed = this.json.admin.photoBooth.playbackSpeed || 1;

        this.json.admin.startScreenPreviewSize = this.json.admin.startScreenPreviewSize || {width: 1920, height: 1080};

        this.getBackgroundsFromOldConfigs("photoBooth", "sharingBackgroundFiles", "sharingBackground");
        this.getBackgroundsFromOldConfigs("photoBooth", "printingBackgroundFiles", "printBackground");

        if (!this.json.admin.photoBooth.startScreenDesignSharing) {
            this.getBackgroundsFromOldConfigs("photoBooth", "sharingOverlayFiles", "sharingOverlay");
        }

        this.getBackgroundsFromOldConfigs("gifBooth", "sharingBackgroundFiles", "sharingBackground");
        this.getBackgroundsFromOldConfigs("gifBooth", "printingBackgroundFiles", "printBackground");

        if (!this.json.admin.gifBooth.startScreenDesignSharing) {
            this.getBackgroundsFromOldConfigs("gifBooth", "sharingOverlayFiles", "sharingOverlay");
        }

        this.getBackgroundsFromOldConfigs("boomerangBooth", "sharingBackgroundFiles", "sharingBackground");
        this.getBackgroundsFromOldConfigs("boomerangBooth", "printingBackgroundFiles", "printBackground");

        if (!this.json.admin.boomerangBooth.startScreenDesignSharing) {
            this.getBackgroundsFromOldConfigs("boomerangBooth", "sharingOverlayFiles", "sharingOverlay");
        }


        if (this.json.admin.showQRCode === undefined) {
            this.json.admin.showQRCode = true;
        }
        this.ensureIndividualText();
        this.ensureOfflineQrCode();

        if (this.json.admin.isAdvanced === undefined) {
            this.json.admin.startScreenBackgroundMode = this.json.admin.startScreenBackgroundMode || "color";
            this.json.admin.startScreenBackgroundColor = this.json.admin.startScreenBackgroundColor || "#ffffff";
            this.json.admin.startScreenImageStyle = this.json.admin.startScreenImageStyle || "stretch";
            this.json.admin.startScreenLogo = this.json.admin.startScreenLogo || {
                enabled: true,
                file: {},
                position: 2,
                size: 30
            };
            this.json.admin.photoBooth.printingLogos = [{
                width: "75%",
                left: "12.5%",
                bottom: "6%",
                height: "15%"
            }];
            this.json.admin.gifBooth.printingLogos = [
                {
                    bottom: "2.5%",
                    height: "10%",
                    width: "40%",
                    left: "5%"
                },
                {
                    bottom: "2.5%",
                    height: "10%",
                    width: "40%",
                    right: "5%"
                }
            ];
            this.json.admin.boomerangBooth.printingLogos = [
                {
                    bottom: "2.5%",
                    height: "10%",
                    width: "40%",
                    left: "5%"
                },
                {
                    bottom: "2.5%",
                    height: "10%",
                    width: "40%",
                    right: "5%"
                }
            ];
        }
        if (this.json.admin.startScreenLiveView === undefined) {
            this.json.admin.startScreenLiveView = {
                enabled: false,
                backgroundOpacity: 50,
                iconsOpacity: 100
            };
        }

        this.json.client.emailTextRow = this.json.client.emailTextRow || this.json.client.emailText;

        this.checkDesignForAllModes();
        this.json.admin.scenes = this.toViewValue.transformScenes(this.json.admin.scenes);
        this.json.admin.ai.faceSwap.faceSwapScenes = this.toViewValue.transformScenes(this.json.admin.ai.faceSwap.faceSwapScenes, false, {
            height: this.json.admin.general.height,
            width: this.json.admin.general.width
        });
        Object.assign(this.json.admin.ai.faceSwap.faceSwapScenes, {
            layoutHeight: this.json.admin.general.height,
            layoutWidth: this.json.admin.general.width
        });

        this.json.admin.ai.faceSwap.faceSwapOverlays = this.toViewValue.transformScenes(this.json.admin.ai.faceSwap.faceSwapOverlays, false, {
            height: this.json.admin.general.height,
            width: this.json.admin.general.width
        });
        Object.assign(this.json.admin.ai.faceSwap.faceSwapOverlays, {
            layoutHeight: this.json.admin.general.height,
            layoutWidth: this.json.admin.general.width
        });

    }

    // Old config check after implementing animated backgrounds and overlays
    getBackgroundsFromOldConfigs(config, arr, file) {
        if (!config && !this.json.admin[config]) {
            return;
        }

        if (this.json.admin[config][file] && this.json.admin[config][file].attachment_file && !this.json.admin[config][arr]) {
            this.json.admin[config][arr] = [];
            this.json.admin[config][arr].push(this.json.admin[config][file].attachment_file);
        }
    }

    getCanvasTitle(value, type) {
        if (this.json && this.json.admin && !this.json.admin.liveView) {
            this.json.admin.liveView = this.toViewValue.getDefaultLiveViewData();
        }
        if (this.json && this.json.admin.liveView && this.json.admin.liveView.liveViewMode === "fullscreen" && type) {
            return this.json.admin.general.screenOrientation === "portrait" && this.json.admin.liveView.liveViewMode === "fullscreen" ? "2:3" : `${this.json.admin.general.aspectRatio.width}:${this.json.admin.general.aspectRatio.height}`;
        }

        let config = type ? this.viewValue[value].digitalFormat : this.viewValue[value];
        switch (config.layoutAspectRatio) {
            case "landscape":
                return "3:2";
            case "portrait":
                return "2:3";
            case "landscape_4_3":
                return "4:3";
            case "portrait_3_4":
                return "3:4";
            case "square":
                return "1:1";
            case "custom":
                return `${config.customPhotoWidth}:${config.customPhotoHeight}`;
            default:
                // A1–A6, 10×15, 15×10, 15×20, 20×15
                return getFormatShortLabel(config.layoutAspectRatio) || "";
        }
    }

    getDigitalCanvasTitle(value, type) {
        if (this.json && this.json.admin && !this.json.admin.liveView) {
            this.json.admin.liveView = this.toViewValue.getDefaultLiveViewData();
        }
        if (this.json && this.json.admin.liveView && this.json.admin.liveView.liveViewMode === "fullscreen" && type) {
            return this.json.admin.general.screenOrientation === "portrait" && this.json.admin.liveView.liveViewMode === "fullscreen" && !this.json.admin.ai.faceSwap.active ? "2:3" : `${this.json.admin.general.aspectRatio.width}:${this.json.admin.general.aspectRatio.height}`;
        }

        let config = type ? this.viewValue[value].digitalFormat : this.viewValue[value];
        switch (config.layoutAspectRatio) {
            case "landscape":
                return "3:2";
            case "portrait":
                return "2:3";
            case "landscape_4_3":
                return "4:3";
            case "portrait_3_4":
                return "3:4";
            case "square":
                return "1:1";
            case "custom":
                return `${config.customPhotoWidth}:${config.customPhotoHeight}`;
        }

    }

    checkOption(value, mode) {
        let config = this.json.admin[value];
        if (mode === "printing" && !config.allowPrinting) {
            config.allowSharePrintFile = false;
        }
        if (mode === "sharing" && this.json.admin.scenes.enabled) {
            config.allowSharing = true;
        }
    }

    formBackground(v) {
        console.log(this.json.admin.offlineForms, v);
    }

    isStepDisabled(value) {
        return (this.json && this.json.admin && this.json.admin[value] && this.json.admin[value].enabled) ? "false" : "true";
    }

    canExitScreen(value, booth) {
        let config = this.viewValue[value];
        if (document.getElementsByClassName("red-border").length) {
            return false;
        }
        // Check custom photo width
        // if (config.photoCaptureRatio === "custom") {
        //     Object.assign(config,{
        //         customPhotoHeight: this.toViewValue.detectAspectRatioObject(config.customPhotoWidth,config.customPhotoHeight).h,
        //         customPhotoWidth:this.toViewValue.detectAspectRatioObject(config.customPhotoWidth,config.customPhotoHeight).w});
        // }
        if (config.photoCaptureRatio === "custom" && (!config.customPhotoHeight || !config.customPhotoWidth)) {
            return false;
        }

        // Check sharing area
        if (this.json && this.json.admin && this.json.admin[booth] && this.json.admin[booth].allowSharing) {
            // if (config.digitalFormat.layoutAspectRatio === "custom") {
            //     Object.assign(config.digitalFormat,{customPhotoHeight: this.toViewValue.detectAspectRatioObject(config.digitalFormat.layoutWidth,config.digitalFormat.layoutHeight).h,customPhotoWidth:this.toViewValue.detectAspectRatioObject(config.digitalFormat.layoutWidth,config.digitalFormat.layoutHeight).w});
            // }
            if (config.digitalFormat.layoutAspectRatio === "custom" && (!config.digitalFormat.customPhotoHeight || !config.digitalFormat.customPhotoWidth)) {
                return false;
            }

            let valid = true;
            forEach(config.digitalFormat.photos, (ph) => {
                if ((!ph.width && ph.width !== 0) || (!ph.height && ph.height !== 0) || (!ph.y && ph.y !== 0) || (!ph.x && ph.x !== 0) || (!ph.angle && ph.angle !== 0)) {
                    valid = false;
                }
            });

            if (!valid) return false;
        }

        // Check printing photos
        if (this.json && this.json.admin && this.json.admin[booth] && this.json.admin[booth].allowPrinting) {
            let valid = true;
            forEach(config.photosArr, (ph) => {
                if ((!ph.width && ph.width !== 0) || (!ph.height && ph.height !== 0) || (!ph.y && ph.y !== 0) || (!ph.x && ph.x !== 0) || (!ph.angle && ph.angle !== 0)) {
                    valid = false;
                }
            });
            if (!valid) return false;

            // Check stripped photos
            if (config.strip) {
                let valid = true;
                forEach(config.strippedPhotosArr, (ph) => {
                    if ((!ph.width && ph.width !== 0) || (!ph.height && ph.height !== 0) || (!ph.y && ph.y !== 0) || (!ph.x && ph.x !== 0) || (!ph.angle && ph.angle !== 0)) {
                        valid = false;
                    }
                });
                if (!valid) return false;
            }
        }
        return true;
    }

    canExitSharingScreen() {
        let val = this.json && this.json.admin ? this.json.admin : null;
        if (!val || !val.outputSettings) {
            return true;
        }
        if (val.offlineQrCode && val.offlineQrCode.enabled && !this.isOfflineQrLayoutValid(val.offlineQrCode)) {
            return false;
        }

        // Allow exit when all sharing methods are disabled (email, SMS, QR)
        if (!val.outputSettings.enableEmailSharing && !val.outputSettings.enableSmsSharing && !val.showQRCode) {
            return true;
        }

        // Check for showQRCode: if active, ignore email and SMS verification
        if (val.showQRCode) {
            // We only check disclaimers and the form, if any.
            if (this.json.client.emailDisclaimer && this.json.client.emailDisclaimerText === undefined ||
                this.json.client.smsDisclaimer && this.json.client.smsDisclaimerText === undefined) {
                return false;
            }
            if (this.formSharing && this.formSharing.$invalid) {
                return false;
            }
            return true;
        }

        // If showQRCode is not active, we perform the old verification via email and SMS
        if (!val.outputSettings.enableEmailSharing && !val.outputSettings.enableSmsSharing) {
            if ((val.photoBooth && val.photoBooth.enabled && val.photoBooth.allowSharing) ||
                (val.gifBooth && val.gifBooth.enabled && val.gifBooth.allowSharing) ||
                (val.boomerangBooth && val.boomerangBooth.enabled && val.boomerangBooth.allowSharing) ||
                (val.videoBooth && val.videoBooth.enabled && val.videoBooth.allowSharing)) {
                return false;
            } else {
                return true;
            }
        }

        if (this.json.client.emailDisclaimer && this.json.client.emailDisclaimerText === undefined ||
            this.json.client.smsDisclaimer && this.json.client.smsDisclaimerText === undefined) {
            return false;
        }

        if (this.formSharing && this.formSharing.$invalid) {
            return false;
        }
        return true;
    }

    canExitForms() {
        if (!this.json) {
            return true;
        }
        if (this.json.admin.offlineForms && this.json.admin.offlineForms.enabled && this.formOfflineForms) {
            return this.formOfflineForms && this.formOfflineForms.$valid;
        } else {
            return true;
        }
    }

    onOrientationChange() {
        let width, height;

        width = this.json.admin.general.width;
        height = this.json.admin.general.height;

        this.json.admin.general.width = height;
        this.json.admin.general.height = width;
        if (this.json.admin.liveView.liveViewMode === "fullscreen") {
            if (this.json.admin.general.screenOrientation === "portrait" && !this.json.admin.scenes.enabled) {
                this.viewValue.singlePhoto.digitalFormat.layoutAspectRatio = "portrait";
                this.json.admin.scenes.sceneLayoutAspectRatio = "portrait";
                this.json.admin.scenes.customLayoutHeight = null;
                this.json.admin.scenes.customLayoutWidth = null;
                this.json.admin.scenes.layoutWidth = this.json.admin.general.width;
                this.json.admin.scenes.layoutHeight = this.json.admin.general.height;
                this.json.admin.scenes.photoAspectRatio = "portrait";
                this.json.admin.scenes.customPhotoHeight = null;
                this.json.admin.scenes.customLayoutWidth = null;
                this.json.admin.scenes.photos = [{
                    width: 333,
                    height: 500,
                    x: 0,
                    y: 0,
                    angle: 0,
                    image: true
                }];
                this.json.admin.ai.faceSwap.faceSwapScenes.sceneLayoutAspectRatio = "portrait";
                this.json.admin.ai.faceSwap.faceSwapScenes.customLayoutHeight = null;
                this.json.admin.ai.faceSwap.faceSwapScenes.customLayoutWidth = null;
                this.json.admin.ai.faceSwap.faceSwapScenes.layoutWidth = this.json.admin.general.width;
                this.json.admin.ai.faceSwap.faceSwapScenes.layoutHeight = this.json.admin.general.height;
                this.json.admin.ai.faceSwap.faceSwapScenes.photoAspectRatio = "portrait";
                this.json.admin.ai.faceSwap.faceSwapScenes.customPhotoHeight = null;
                this.json.admin.ai.faceSwap.faceSwapScenes.customLayoutWidth = null;
                this.json.admin.ai.faceSwap.faceSwapScenes.enable_animated_bg_files = false;
                this.json.admin.ai.faceSwap.faceSwapScenes.photos = [{
                    width: 333,
                    height: 500,
                    x: 0,
                    y: 0,
                    angle: 0,
                    image: true
                }];

                this.json.admin.ai.faceSwap.faceSwapOverlays.sceneLayoutAspectRatio = "portrait";
                this.json.admin.ai.faceSwap.faceSwapOverlays.customLayoutHeight = null;
                this.json.admin.ai.faceSwap.faceSwapOverlays.customLayoutWidth = null;
                this.json.admin.ai.faceSwap.faceSwapOverlays.layoutWidth = this.json.admin.general.width;
                this.json.admin.ai.faceSwap.faceSwapOverlays.layoutHeight = this.json.admin.general.height;
                this.json.admin.ai.faceSwap.faceSwapOverlays.photoAspectRatio = "portrait";
                this.json.admin.ai.faceSwap.faceSwapOverlays.customPhotoHeight = null;
                this.json.admin.ai.faceSwap.faceSwapOverlays.customLayoutWidth = null;
                this.json.admin.ai.faceSwap.faceSwapOverlays.enable_animated_bg_files = false;
                this.json.admin.ai.faceSwap.faceSwapOverlays.photos = [{
                    width: 333,
                    height: 500,
                    x: 0,
                    y: 0,
                    angle: 0,
                    image: true
                }];

                this.json.admin.general.aspectRatio = {width: 2, height: 3};
            }
        }
        this.updateViewMode();
    }

    updateViewMode() {
        if (this.json.admin.liveView.liveViewMode === "fullscreen") {

            this.json.admin.scenes.layoutWidth = this.json.admin.general.width;
            this.json.admin.scenes.layoutHeight = this.json.admin.general.height;
            if (this.json.admin.general.screenOrientation === "portrait" && !this.json.admin.scenes.enabled) {
                this.viewValue.singlePhoto.digitalFormat.layoutAspectRatio = "portrait";
                this.json.admin.scenes.sceneLayoutAspectRatio = "portrait";
                this.json.admin.scenes.customLayoutHeight = null;
                this.json.admin.scenes.customLayoutWidth = null;
                // this.json.admin.scenes.layoutHeight = 500;
                // this.json.admin.scenes.layoutWidth = 333;
                this.json.admin.scenes.photoAspectRatio = "portrait";
                this.json.admin.scenes.customPhotoHeight = null;
                this.json.admin.scenes.customLayoutWidth = null;
                this.json.admin.scenes.photos = [{
                    width: 333,
                    height: 500,
                    x: 0,
                    y: 0,
                    angle: 0,
                    image: true
                }];
                this.json.admin.general.aspectRatio = {width: 2, height: 3};
            } else {
                this.json.admin.scenes.sceneLayoutAspectRatio = "custom";
                this.viewValue.singlePhoto.digitalFormat.layoutAspectRatio = "custom";
                this.json.admin.scenes.photoAspectRatio = "custom";
                this.json.admin.scenes.sceneLayoutAspectRatio = "custom";
                this.json.admin.scenes.photos = [{
                    width: this.json.admin.general.width,
                    height: this.json.admin.general.height,
                    x: 0,
                    y: 0,
                    angle: 0,
                    image: true
                }];
                this.json.admin.general.aspectRatio = this.toBoothValue.transformPhotoAspectRatio("singlePhoto", this.viewValue);

                this.json.admin.scenes.customLayoutWidth = this.json.admin.general.aspectRatio.width;
                this.json.admin.scenes.customLayoutHeight = this.json.admin.general.aspectRatio.height;
            }
            this.changeScenes();
        } else {
            this.changePhotosAspectRatio("singlePhoto");
            this.changePhotosAspectRatio("gif");
            this.changePhotosAspectRatio("boomerang");
        }
        this.saveDataInConfig();
    }

    resetStep(params) {
        let dpi = 300;
        let confirm = this.$mdDialog.confirm()
            .title("Are you sure you want to reset to the default settings?")
            .textContent("Resetting will delete all the work you've done so far")
            .ariaLabel("Confirm")
            .ok("RESET")
            .cancel("Cancel");
        this.$mdDialog.show(confirm).then(() => {
            switch (params) {
                case "standby":
                    this.json.admin.standbyScreen = false;
                    this.json.admin.standbyScreenActivateTime = 30;
                    this.json.admin.standbyScreenImage = null;
                    break;
                case "start-screen":
                    this.json.admin.photoBooth.enabled = true;
                    this.json.admin.disablePostFilters = true;
                    this.json.admin.gifBooth.enabled = true;
                    this.json.admin.filtersNamesShow = false;
                    this.json.admin.boomerangBooth.enabled = true;
                    this.json.admin.videoBooth.enabled = false;
                    this.json.admin.startScreenAreas.single = {width: 40, height: 40, x: 25, y: 60};
                    this.json.admin.startScreenAreas.gif = {width: 40, height: 40, x: 50, y: 60};
                    this.json.admin.startScreenAreas.boomerang = {width: 40, height: 40, x: 75, y: 60};
                    this.json.admin.startScreenAreas.video = {width: 40, height: 40, x: 95, y: 60};
                    this.json.admin.startScreenAreas.random = Math.random();
                    this.json.admin.startScreenDefaultIcons = false;
                    this.json.admin.startScreenBackground = null;
                    this.json.admin.startScreenReturnTime = 90;
                    this.json.admin.cameraPresetName = "Default";
                    this.json.admin.startScreenPreviewSize = {
                        width: 1920,
                        height: 1080
                    };
                    this.animatedIcons = false;
                    this.json.admin.startScreenLiveView = {
                        enabled: false,
                        backgroundOpacity: 50,
                        iconsOpacity: 100
                    };
                    this.json.admin.startScreenBackgroundMode = "color";
                    this.json.admin.startScreenBackgroundColor = "#ffffff";
                    this.json.admin.startScreenImageStyle = "stretch";
                    this.json.admin.startScreenLogo = {
                        enabled: true,
                        file: {},
                        position: 2,
                        size: 30
                    };
                    this.json.admin.disableSceneAfterCapture = false;
                    break;
                case "ai":
                    this.json.admin.ai = {
                        customLogo: {
                            active: false,
                            customLogoFile: null
                        },
                        replicate: {
                            replicate_remove_bg_active: false,
                            replicate_api_key: "",
                            replicate_prompting_active: false,
                            replicate_prompt_quality: REPLICATE_PROMPT_DEFAULT_QUALITY,
                            replicate_model: REPLICATE_PROMPT_MODELS[REPLICATE_PROMPT_DEFAULT_QUALITY]
                        },
                        wandershareApi: {
                            active: false,
                            aging_active: false,
                            aging_api_key: "",
                            aging_api_secret: "",
                            aging_age_slider_allowed: false,
                            aging_fixed_age: 0,
                            aging_priority: 0
                        },
                        faceSwap: {
                            active: false,
                            face_swap_active: false,
                            face_swap_api_key: "",
                            face_swap_multi_active: false,
                            scenesNamesShow: false,
                            scenesBtnTitleShow: false,
                            sceneBtnTitle: "",
                            faceSwapScenes: {
                                enabled: false,
                                enable_animated_bg_files: false,
                                randomizeSceneSelection: false,
                                finalSize: {
                                    width: 333,
                                    height: 500
                                },
                                internalPictureSize: {
                                    width: 333,
                                    height: 500
                                },
                                layoutAspectRatio: {
                                    width: null,
                                    height: null
                                },
                                pictureAspectRatio: {
                                    width: null,
                                    height: null
                                },
                                internalPicturePosition: {
                                    topX: 0,
                                    topY: 0
                                },
                                internalPictureRotation: 0,
                                scenesList: [],
                                scenesNamesShow: false,
                                opacity: 50,
                                scenesBtnTitleShow: false,
                                sceneBtnTitle: ""
                            },
                            faceSwapOverlays: {
                                enabled: false,
                                enable_animated_bg_files: false,
                                randomizeSceneSelection: false,
                                finalSize: {
                                    width: 333,
                                    height: 500
                                },
                                internalPictureSize: {
                                    width: 333,
                                    height: 500
                                },
                                layoutAspectRatio: {
                                    width: null,
                                    height: null
                                },
                                pictureAspectRatio: {
                                    width: null,
                                    height: null
                                },
                                internalPicturePosition: {
                                    topX: 0,
                                    topY: 0
                                },
                                internalPictureRotation: 0,
                                scenesList: [],
                                scenesNamesShow: false,
                                opacity: 50,
                                scenesBtnTitleShow: false,
                                sceneBtnTitle: ""
                            },
                        },
                    };
                    this.json.admin.ai.faceSwap.faceSwapScenes.photos = [{
                        width: 1920,
                        height: 1080,
                        x: 0,
                        y: 0,
                        angle: 0,
                        image: true
                    }];
                    this.json.admin.ai.faceSwap.faceSwapOverlays.photos = [{
                        width: 1920,
                        height: 1080,
                        x: 0,
                        y: 0,
                        angle: 0,
                        image: true
                    }];
                    // Reset removeBg
                    this.json.admin.removeBg = {active: false};
                    this.setRemoveBgData({active: false});
                    // Reset replicate
                    this.json.admin.ai.replicate = {
                        replicate_remove_bg_active: false,
                        replicate_api_key: "",
                        replicate_prompting_active: false,
                        replicate_prompt_quality: REPLICATE_PROMPT_DEFAULT_QUALITY,
                        replicate_model: REPLICATE_PROMPT_MODELS[REPLICATE_PROMPT_DEFAULT_QUALITY]
                    };
                    this.setReplicateData({replicate_remove_bg_active: false});
                    // Reset remaker
                    this.json.admin.ai.remaker = {remaker_remove_bg_active: false, remaker_api_key: ""};
                    this.setRemakerData({remaker_remove_bg_active: false});
                    // Reset removeBgOffline
                    if (this.json.admin.removeBgOffline) {
                        this.json.admin.removeBgOffline.remove_bg_offline_active = false;
                        this.setLocalRemoveBgState({remove_bg_offline_active: false});
                    }
                    // Reset unscreen
                    if (this.json.admin.unscreen) {
                        this.json.admin.unscreen.us_active = false;
                        this.setUnscreenData({us_active: false});
                    }
                    break;
                case "live-view":
                    this.json.admin.liveView = {
                        enabled: true,
                        forAllScenes: true,
                        liveViewMode: "regular",
                        liveViewPreviewSize: {
                            width: 1920,
                            height: 1080
                        },
                        liveViewBackgroundMode: "color",
                        liveViewBackgroundColor: "#ffffff",
                        liveViewBackground: "",
                        filter: {
                            enabled: true,
                            liveViewBackgroundMode: "color",
                            liveViewBackgroundColor: "#ffffff",
                            liveViewBackground: "",
                        },
                        output: {
                            enabled: true,
                            liveViewBackgroundMode: "color",
                            liveViewBackgroundColor: "#ffffff",
                            liveViewBackground: "",
                        },
                        shouldOutputPhotoMirrored: true,
                        hideOutputPictureVideo: false,
                        hideSharingOptions: false,
                        customButtons: {
                            active: false,
                            generalUiButtonsSettings: {
                                back: {width: 4.5, height: 4.5, x: 8, y: 7},
                                sharingBackBtn: {width: 3.5, height: 3.5, x: 8, y: 7},
                                capture: {width: 5.5, height: 5.5, x: 92, y: 50},
                                proceed: {width: 5.5, height: 5.5, x: 92, y: 50},
                                email: {width: 5.5, height: 5.5, x: 16.5, y: 88},
                                sms: {width: 3.5, height: 3.5, x: 35, y: 88},
                                print: {width: 3.5, height: 3.5, x: 45, y: 88},
                                done: {width: 6, height: 6, x: 89, y: 92.5},
                                qrCode: {width: 7, height: 7, x: 50, y: 11,text:"Scan me to download",textSize: 12,textColor: "black"},
                                close: {width: 3.5, height: 3.5, x: 5, y: 28},
                                next: {width: 5, height: 5, x: 93, y: 50},
                                closeError: {width: 5, height: 5, x: 93, y: 50},
                            },
                            captureModeButtonsSettings: {
                                single: {width: 3.5, height: 3.5, x: 8, y: 44},
                                gif: {width: 3.5, height: 3.5, x: 8, y: 49},
                                boomerang: {width: 3.5, height: 3.5, x: 8, y: 55},
                                scene: {scenePopupColor: "#FFF", width: 36, height: 48, x: 50, y: 57},
                                filter: {width: 5, height: 5, x: 8, y: 38},
                            }
                        },
                        customSceneScreen: {
                            enabled: false,
                            numberOfScenes: 1,
                            background: {},
                            touchpoints: []
                        }
                    };
                    break;
                case "scenes":
                    this.json.admin.scenes.enabled = false;
                    this.json.admin.scenes.scenesNamesShow = false;
                    this.json.admin.scenes.randomizeSceneSelection = false;
                    this.json.admin.scenes.sceneLayoutAspectRatio = "custom";
                    this.json.admin.scenes.customLayoutHeight = null;
                    this.json.admin.scenes.customLayoutWidth = null;
                    this.json.admin.scenes.layoutHeight = 1080;
                    this.json.admin.scenes.layoutWidth = 1920;
                    this.json.admin.scenes.photoAspectRatio = "custom";
                    this.json.admin.scenes.customPhotoHeight = null;
                    this.json.admin.scenes.customLayoutWidth = null;
                    this.json.admin.scenes.photos = [{
                        width: 1920,
                        height: 1080,
                        x: 0,
                        y: 0,
                        angle: 0,
                        image: true
                    }];
                    this.json.admin.scenes.opacity = 50;
                    this.json.admin.scenes.scenesBtnTitleShow = false;
                    this.json.admin.scenes.sceneBtnTitle = "";
                    delete this.json.admin.scenes.scenesList;
                    this.json.admin.scenes.scenesList = [];

                    this.json.admin.photoBooth.sharingBackgroundFiles = [];
                    this.json.admin.photoBooth.sharingOverlayFiles = [];

                    this.json.admin.gifBooth.sharingBackgroundFiles = [];
                    this.json.admin.gifBooth.sharingOverlayFiles = [];

                    this.json.admin.boomerangBooth.sharingBackgroundFiles = [];
                    this.json.admin.boomerangBooth.sharingOverlayFiles = [];

                    this.json.admin.photoBooth.savedData.sharingBackgroundFiles = [];
                    this.json.admin.photoBooth.savedData.sharingOverlayFiles = [];


                    this.json.admin.gifBooth.savedData.sharingBackgroundFiles = [];
                    this.json.admin.gifBooth.savedData.sharingOverlayFiles = [];

                    this.json.admin.boomerangBooth.savedData.sharingBackgroundFiles = [];
                    this.json.admin.boomerangBooth.savedData.sharingOverlayFiles = [];

                    break;
                case "single-photo":
                    this.viewValue.singlePhoto = {
                        photoCaptureRatio: "custom",
                        customPhotoWidth: null,
                        customPhotoHeight: null,
                        layoutAspectRatio: "portrait",
                        printCanvasWidth: (6 * dpi) + 40,
                        printCanvasHeight: (4 * dpi) + 44,
                        strip: false,
                        photosArr: [],
                        strippedPhotosArr: [],
                        digitalFormat: {
                            layoutAspectRatio: "custom",
                            photos: [{}],
                            customPhotoWidth: null,
                            customPhotoHeight: null,
                            layoutHeight: 500,
                            layoutWidth: 500
                        }
                    };
                    this.changeSinglePhotoLayoutRatio("singlePhoto");
                    this.setPhotosForPrint("singlePhoto");
                    this.json.admin.photoBooth.allowSharing = true;
                    this.json.admin.photoBooth.allowSharePrintFile = true;
                    this.json.admin.photoBooth.allowPrinting = true;
                    this.json.admin.photoBooth.printWithoutScenes = false;
                    this.json.admin.photoBooth.showPrinterBleeds = false;
                    this.json.admin.photoBooth.printingBackgroundFiles = [];
                    this.json.admin.photoBooth.printingOverlayFiles = [];
                    this.json.admin.photoBooth.sharingBackgroundFiles = [];
                    this.json.admin.photoBooth.savedData.sharingBackgroundFiles = [];
                    this.json.admin.photoBooth.savedData.sharingOverlayFiles = [];
                    this.json.admin.photoBooth.sharingBackgroundFilesValidRatio = null;
                    this.json.admin.photoBooth.sharingOverlayFiles = [];
                    this.json.admin.photoBooth.sharingOverlayFilesValidRatio = null;
                    this.json.admin.photoBooth.countdownTime = 5;
                    this.json.admin.photoBooth.playbackSpeed = 1.5;

                    this.json.admin.photoBooth.startScreenDesignSharing = !(this.$auth.isRole("Admin") || this.$auth.isRole("Manager"));
                    this.json.admin.photoBooth.showLiveViewOverlays = true;
                    this.json.admin.photoBooth.shouldOutputPhotoMirrored = true;
                    this.checkStartScreenDesign("photoBooth");
                    this.json.admin.photoBooth.printingLogos = [{
                        width: "75%",
                        left: "12.5%",
                        bottom: "6%",
                        height: "15%"
                    }];
                    break;
                case "gif":
                    this.viewValue.gif = cloneDeep(this.storedConfig.gif);
                    this.viewValue.gif.strip = true;
                    this.changeSinglePhotoLayoutRatio("gif");
                    this.setPhotosForPrint("gif");
                    this.activatePhotoStrips("gif");
                    this.json.admin.gifBooth.allowSharing = true;
                    this.json.admin.gifBooth.allowSharePrintFile = true;
                    this.json.admin.gifBooth.allowPrinting = true;
                    this.json.admin.gifBooth.printWithoutScenes = false;
                    this.json.admin.gifBooth.showPrinterBleeds = false;
                    this.json.admin.gifBooth.printingBackgroundFiles = [];
                    this.json.admin.gifBooth.printingOverlayFiles = [];
                    this.json.admin.gifBooth.sharingBackgroundFiles = [];
                    this.json.admin.gifBooth.savedData.sharingBackgroundFiles = [];
                    this.json.admin.gifBooth.savedData.sharingOverlayFiles = [];
                    this.json.admin.gifBooth.sharingBackgroundFilesValidRatio = null;
                    this.json.admin.gifBooth.sharingOverlayFiles = [];
                    this.json.admin.gifBooth.sharingOverlayFilesValidRatio = null;
                    this.json.admin.gifBooth.countdownTime = 5;
                    this.json.admin.gifBooth.playbackSpeed = 1.5;

                    this.json.admin.gifBooth.startScreenDesignSharing = !(this.$auth.isRole("Admin") || this.$auth.isRole("Manager"));
                    this.json.admin.gifBooth.showLiveViewOverlays = true;
                    this.json.admin.gifBooth.shouldOutputPhotoMirrored = true;
                    this.checkStartScreenDesign("gifBooth");
                    this.json.admin.gifBooth.printingLogos = [
                        {
                            bottom: "2.5%",
                            height: "10%",
                            width: "40%",
                            left: "5%"
                        },
                        {
                            bottom: "2.5%",
                            height: "10%",
                            width: "40%",
                            right: "5%"
                        }
                    ];
                    break;
                case "boomerang":
                    this.viewValue.boomerang = cloneDeep(this.storedConfig.boomerang);
                    this.viewValue.boomerang.strip = true;
                    this.changeSinglePhotoLayoutRatio("boomerang");
                    this.setPhotosForPrint("boomerang");
                    this.activatePhotoStrips("boomerang");
                    this.json.admin.boomerangBooth.allowSharing = true;
                    this.json.admin.boomerangBooth.allowSharePrintFile = true;
                    this.json.admin.boomerangBooth.allowPrinting = true;
                    this.json.admin.boomerangBooth.printWithoutScenes = false;
                    this.json.admin.boomerangBooth.showPrinterBleeds = false;
                    this.json.admin.boomerangBooth.printingBackgroundFiles = [];
                    this.json.admin.boomerangBooth.printingOverlayFiles = [];
                    this.json.admin.boomerangBooth.sharingBackgroundFiles = [];
                    this.json.admin.boomerangBooth.savedData.sharingBackgroundFiles = [];
                    this.json.admin.boomerangBooth.savedData.sharingOverlayFiles = [];
                    this.json.admin.boomerangBooth.sharingBackgroundFilesValidRatio = null;
                    this.json.admin.boomerangBooth.sharingOverlayFiles = [];
                    this.json.admin.boomerangBooth.sharingOverlayFilesValidRatio = null;
                    this.json.admin.boomerangBooth.countdownTime = 5;
                    this.json.admin.boomerangBooth.playbackSpeed = 1.5;
                    this.json.admin.boomerangBooth.captureTime = 1.5;

                    this.json.admin.boomerangBooth.startScreenDesignSharing = !(this.$auth.isRole("Admin") || this.$auth.isRole("Manager"));
                    this.json.admin.boomerangBooth.showLiveViewOverlays = true;
                    this.json.admin.boomerangBooth.shouldOutputPhotoMirrored = true;
                    this.checkStartScreenDesign("boomerangBooth");
                    this.json.admin.boomerangBooth.printingLogos = [
                        {
                            bottom: "2.5%",
                            height: "10%",
                            width: "40%",
                            left: "5%"
                        },
                        {
                            bottom: "2.5%",
                            height: "10%",
                            width: "40%",
                            right: "5%"
                        }
                    ];
                    break;
                case "video":
                    this.viewValue.video = cloneDeep(this.storedConfig.video);
                    this.json.admin.videoBooth.allowSharing = true;
                    this.json.admin.videoBooth.countdownTime = 5;
                    this.json.admin.videoBooth.playbackSpeed = 1;
                    this.json.admin.videoBooth.captureTime = 1.5;
                    this.json.admin.videoBooth.sliderValue = 300;
                    this.json.admin.videoBooth.shouldOutputPhotoMirrored = true;
                    this.json.admin.videoBooth.enableChromakey = false;
                    this.json.admin.videoBooth.enableSoundRecording = false;
                    this.clearVideoChromakeyFromScenes();
                    break;
                case "sharing":
                    this.json.client = {
                        emailSender: "photos@upreach.com",
                        advancedHtmlInput: false,
                        emailText: "",
                        emailTextRow: "",
                        emailTemplate: "English",
                        emailSubject: "Your UpReach photo",
                        allowEmailAttachments: true,
                        emailDisclaimer: false,
                        emailDisclaimerRequire: false,
                        emailDisclaimerText: "",
                        emailTitle: "Hey, nice snap!",
                        emailTextNoHtml: "Here you are, please find attached your awesome UpReach photo. Thank you so much for visiting us!\n Scroll down to download & share your image.",
                        smsText: "Hey, nice snap!\nVisit the live gallery to download & share your UpReach photo: ",
                        smsDisclaimer: false,
                        smsDisclaimerRequire: false,  //Add this as toggle
                        smsDisclaimerText: ""

                    };
                    this.json.admin.maxCountOfPrintCopies = 5;
                    this.json.admin.enableAutoPrint = false;
                    this.json.admin.autoPrintCopies = 1;
                    this.json.admin.outputSettings = {
                        enableEmailSharing: true,
                        enableSmsSharing: false,
                    };
                    this.json.admin.showQRCode = true;
                    this.json.admin.offlineQrCode = this.getDefaultOfflineQrCode();
                    this.json.client.emailTextRow = this.emailTemplates.getTemplate("english", this.json.client.emailTitle, this.json.client.emailTextNoHtml);
                    break;
                case "individual-text":
                    this.json.admin.individualText = getDefaultIndividualText();
                    break;
                case "forms":
                    this.json.admin.offlineForms.whenOfflineForm = [
                        {
                            "value": "beforePhoto",
                            "label": "before taking photo"
                        },
                        {
                            "value": "afterPhoto",
                            "label": "after taking photo"
                        },
                        {
                            "value": "optional",
                            "label": "optional"
                        }
                    ];
                    this.json.admin.offlineForms.formInputs = [
                        {
                            required: true,
                            active: true,
                            title: "Name"
                        },
                        {
                            required: false,
                            active: true,
                            title: "E-Mail"
                        },
                        {
                            required: true,
                            active: true,
                            title: "Phone Number"
                        }
                    ];

                    this.json.admin.offlineForms.customInputs = [
                        {
                            required: false,
                            active: false,
                            title: "",
                            inputMode: "text",
                            choices: [{text: ""}]
                        },
                        {
                            required: false,
                            active: false,
                            title: "",
                            inputMode: "text",
                            choices: [{text: ""}]
                        },
                        {
                            required: false,
                            active: false,
                            title: "",
                            inputMode: "text",
                            choices: [{text: ""}]
                        },
                        {
                            required: false,
                            active: false,
                            title: "",
                            inputMode: "text",
                            choices: [{text: ""}]
                        }, {
                            required: false,
                            active: false,
                            title: "",
                            inputMode: "text",
                            choices: [{text: ""}]
                        },
                    ];
                    this.json.admin.offlineForms.formCheckboxes = [
                        {
                            required: true,
                            active: true,
                            title: "",
                            description: "",
                            usePdf: false,
                            fileName: "",
                            fileUrl: "",
                            fileId: null,
                            isInfobox: false,
                            typeIsCheckbox: true,
                            infoBoxCustomText: "Press here to review the full statement…"
                        },
                        {
                            required: false,
                            active: false,
                            title: "",
                            description: "",
                            usePdf: false,
                            fileName: "",
                            fileUrl: "",
                            fileId: null,
                            isInfobox: false,
                            typeIsCheckbox: true,
                            infoBoxCustomText: "Press here to review the full statement…"
                        },
                        {
                            required: false,
                            active: false,
                            title: "",
                            description: "",
                            usePdf: false,
                            fileName: "",
                            fileUrl: "",
                            fileId: null,
                            isInfobox: false,
                            typeIsCheckbox: true,
                            infoBoxCustomText: "Press here to review the full statement…"
                        },
                        {
                            required: false,
                            active: false,
                            title: "",
                            description: "",
                            usePdf: false,
                            fileName: "",
                            fileUrl: "",
                            fileId: null,
                            isInfobox: false,
                            typeIsCheckbox: true,
                            infoBoxCustomText: "Press here to review the full statement…"
                        },
                        {
                            required: false,
                            active: false,
                            title: "",
                            description: "",
                            usePdf: false,
                            fileName: "",
                            fileUrl: "",
                            fileId: null,
                            isInfobox: false,
                            typeIsCheckbox: true,
                            infoBoxCustomText: "Press here to review the full statement…"
                        },
                    ];
                    this.json.admin.offlineForms.infoBoxCustomize = false;
                    this.json.admin.offlineForms.formCheckboxesTypeLikeRadiobutton = false;

                    this.json.admin.offlineForms.formSignature = {
                        "required": true,
                        "active": false,
                        "title": "Signature"
                    };
                    this.json.admin.offlineForms.backgroundImage = null;
                    this.json.admin.offlineForms.isSendFromForm = false;
                    this.json.admin.offlineForms.enabled = false;
                    break;
            }
        });
    }

    /**
     * Remove buttons from start screen preview when some option disabled
     * @param value
     */
    ensureVideoBoothFields(videoBooth) {
        if (!videoBooth) {
            return;
        }
        if (videoBooth.enableChromakey === undefined) {
            videoBooth.enableChromakey = false;
        }
        if (videoBooth.enableSoundRecording === undefined) {
            videoBooth.enableSoundRecording = false;
        }
    }

    clearVideoChromakeyFromScenes() {
        if (!this.json || !this.json.admin || !this.json.admin.scenes || !this.json.admin.scenes.scenesList) {
            return;
        }
        this.json.admin.scenes.scenesList.forEach((scene) => {
            if (scene.selectedFeature && scene.selectedFeature.value === VIDEO_CHROMAKEY_FEATURE) {
                scene.selectedFeature = null;
            }
        });
    }

    rebuildSceneActiveFeatures() {
        if (!this.json || !this.json.admin) {
            this.sceneActiveFeatures = [];
            return;
        }
        const admin = this.json.admin;
        this.sceneActiveFeatures = [
            {feature: "2d_to_3d", active: !!(admin.ai["2Dto3D"] && admin.ai["2Dto3D"].immersity_2d_to_3d_active)},
            {feature: "faceswap", active: !!admin.ai.faceSwap.active},
            {feature: "multi_faceswap", active: !!admin.ai.faceSwap.face_swap_multi_active},
            {feature: "wandershare_api", active: !!admin.ai.wandershareApi.active},
            {
                feature: "remove_bg_online",
                active: !!(admin.ai.replicate.replicate_remove_bg_active || admin.ai.remaker.remaker_remove_bg_active)
            },
            {
                feature: "replicate_prompting",
                active: !!(admin.ai.replicate.replicate_remove_bg_active && admin.ai.replicate.replicate_prompting_active)
            },
            {feature: "remove_bg_offline", active: !!(admin.removeBgOffline && admin.removeBgOffline.remove_bg_offline_active)},
            {feature: "remove_bg_video", active: !!(admin.unscreen && admin.unscreen.us_active)},
            {
                feature: VIDEO_CHROMAKEY_FEATURE,
                active: !!(admin.videoBooth && admin.videoBooth.enableChromakey)
            }
        ];
    }

    onVideoChromakeyChange() {
        this.rebuildSceneActiveFeatures();
        if (!this.json.admin.videoBooth.enableChromakey) {
            this.clearVideoChromakeyFromScenes();
        }
    }

    onVideoBoothEnabledChange() {
        if (!this.json.admin.videoBooth.enabled) {
            this.json.admin.videoBooth.enableChromakey = false;
            this.clearVideoChromakeyFromScenes();
        }
        this.removeButtons();
    }

    removeButtons() {
        if (!this.json.admin.startScreenAreas) {
            return;
        }
        this.json.admin.startScreenAreas.random = Math.random();

        let photo = this.json.admin.photoBooth.enabled;
        let gif = this.json.admin.gifBooth.enabled;
        let boomerang = this.json.admin.boomerangBooth.enabled;
        let video = this.json.admin.videoBooth.enabled;
        let isTop = this.json.admin.startScreenLogo.position <= 4;
        if (this.json.admin.startScreenAreas.single.width &&
            this.json.admin.startScreenAreas.gif.width &&
            this.json.admin.startScreenAreas.boomerang.width &&
            this.json.admin.startScreenAreas.video.width) {
            return;
        }
        this.json.admin.startScreenAreas.single = {width: 40, height: 40, x: 25, y: (isTop ? 60 : 40)};
        this.json.admin.startScreenAreas.gif = {width: 40, height: 40, x: 50, y: (isTop ? 60 : 40)};
        this.json.admin.startScreenAreas.boomerang = {width: 40, height: 40, x: 75, y: (isTop ? 60 : 40)};
        this.json.admin.startScreenAreas.video = {width: 40, height: 40, x: 95, y: (isTop ? 60 : 40)};

        if (photo && gif && !boomerang && video) {
            this.json.admin.startScreenAreas.single.x = 37.5;
            this.json.admin.startScreenAreas.gif.x = 62.5;
            this.json.admin.startScreenAreas.boomerang = {};
            this.json.admin.startScreenAreas.video.x = 75;
        } else if (photo && !gif && boomerang && video) {
            this.json.admin.startScreenAreas.single.x = 37.5;
            this.json.admin.startScreenAreas.gif = {};
            this.json.admin.startScreenAreas.boomerang.x = 62.5;
            this.json.admin.startScreenAreas.video.x = 75;
        } else if (!photo && gif && boomerang && video) {
            this.json.admin.startScreenAreas.single = {};
            this.json.admin.startScreenAreas.gif.x = 37.5;
            this.json.admin.startScreenAreas.boomerang.x = 62.5;
            this.json.admin.startScreenAreas.video.x = 75;
        } else if (!photo && !gif && boomerang && video) {
            this.json.admin.startScreenAreas.single = {};
            this.json.admin.startScreenAreas.gif = {};
            this.json.admin.startScreenAreas.boomerang.x = 50;
            this.json.admin.startScreenAreas.video.x = 75;
        } else if (!photo && gif && !boomerang && video) {
            this.json.admin.startScreenAreas.single = {};
            this.json.admin.startScreenAreas.gif.x = 50;
            this.json.admin.startScreenAreas.boomerang = {};
            this.json.admin.startScreenAreas.video.x = 75;
        } else if (photo && !gif && !boomerang && video) {
            this.json.admin.startScreenAreas.single.x = 50;
            this.json.admin.startScreenAreas.gif = {};
            this.json.admin.startScreenAreas.boomerang = {};
            this.json.admin.startScreenAreas.video.x = 75;

        } else if (photo && gif && boomerang && !video) {
            this.json.admin.startScreenAreas.single.x = 37.5;
            this.json.admin.startScreenAreas.gif.x = 62.5;
            this.json.admin.startScreenAreas.boomerang.x = 50;
            this.json.admin.startScreenAreas.video = {};

        } else if (!photo && !gif && !boomerang && video) {
            this.json.admin.startScreenAreas.single.x = {};
            this.json.admin.startScreenAreas.gif.x = {};
            this.json.admin.startScreenAreas.boomerang.x = {};
            this.json.admin.startScreenAreas.video.x = 75;

        }
    }

    canExitScenes() {
        const checkEnabledScenes = (arr) => {
            let enabled = false;
            forEach(arr, (s) => {
                if (s.enabled) {
                    enabled = true;
                }
            });
            return enabled;
        };

        if (!this.json || !this.json.admin || !this.json.admin.scenes) {
            return;
        }
        if (this.json.admin.scenes &&
            this.json.admin.scenes.enabled &&
            (!this.json.admin.scenes.scenesList.length || !checkEnabledScenes(this.json.admin.scenes.scenesList))) {
            return false;
        }
        let s = this.json.admin.scenes;
        if (!s.sceneLayoutAspectRatio) return false;
        if (!s.layoutHeight) return false;
        if (!s.layoutWidth) return false;
        if (!s.photos ||
            !s.photos[0].width ||
            !s.photos[0].height ||
            (!s.photos[0].x && s.photos[0].x !== 0) ||
            (!s.photos[0].y && s.photos[0].y !== 0)) {
            return false;
        }

        // s.photos = scenes.photos;
        return true;
    }

    ensureCustomSceneScreenConfig() {
        if (!this.json || !this.json.admin || !this.json.admin.liveView) {
            return;
        }
        const liveView = this.json.admin.liveView;
        if (!liveView.customSceneScreen) {
            liveView.customSceneScreen = {};
        }
        if (liveView.customSceneScreen.enabled === undefined) {
            liveView.customSceneScreen.enabled = false;
        }
        if (!Number.isFinite(parseInt(liveView.customSceneScreen.numberOfScenes, 10)) ||
            parseInt(liveView.customSceneScreen.numberOfScenes, 10) < 1) {
            liveView.customSceneScreen.numberOfScenes = 1;
        } else {
            liveView.customSceneScreen.numberOfScenes = parseInt(liveView.customSceneScreen.numberOfScenes, 10);
        }
        if (!liveView.customSceneScreen.background || typeof liveView.customSceneScreen.background !== "object") {
            liveView.customSceneScreen.background = {};
        }
        if (!isArray(liveView.customSceneScreen.touchpoints)) {
            liveView.customSceneScreen.touchpoints = [];
        }
        const nextTouchpoints = [];
        for (let i = 0; i < liveView.customSceneScreen.numberOfScenes; i++) {
            const existing = liveView.customSceneScreen.touchpoints[i] || {};
            nextTouchpoints.push({
                width: 20,
                height: 20,
                x: 15 + ((i % 4) * 22),
                y: 20 + (Math.floor(i / 4) * 25),
                angle: 0,
                scene_title: "",
                custom_icon: null,
                customIcon: null,
                ...existing,
                scene_index: i + 1
            });
        }
        liveView.customSceneScreen.touchpoints = nextTouchpoints;
    }

    ensureGeneralUiButtonsSettings() {
        if (!this.json || !this.json.admin || !this.json.admin.liveView) {
            return;
        }
        const customButtons = this.json.admin.liveView.customButtons;
        if (!customButtons || !customButtons.generalUiButtonsSettings) {
            return;
        }
        const settings = customButtons.generalUiButtonsSettings;
        if (settings.proceed) {
            return;
        }
        const defaultProceed = {width: 5.5, height: 5.5, x: 92, y: 50};
        settings.proceed = settings.capture ? cloneDeep(settings.capture) : defaultProceed;
    }

    getCustomSceneScreenMismatch() {
        this.ensureCustomSceneScreenConfig();
        if (!this.json || !this.json.admin || !this.json.admin.liveView || !this.json.admin.liveView.customSceneScreen) {
            return null;
        }
        const customSceneScreen = this.json.admin.liveView.customSceneScreen;
        if (!customSceneScreen.enabled) {
            return null;
        }
        const expectedCount = parseInt(customSceneScreen.numberOfScenes, 10) || 1;
        const scenesList = this.json.admin.scenes && isArray(this.json.admin.scenes.scenesList) ? this.json.admin.scenes.scenesList : [];
        const actualCount = scenesList.length;
        if (expectedCount === actualCount) {
            return null;
        }
        return {
            expectedCount,
            actualCount
        };
    }

    hasCustomSceneScreenMismatch() {
        return !!this.getCustomSceneScreenMismatch();
    }

    getCustomSceneScreenWarningText() {
        const mismatch = this.getCustomSceneScreenMismatch();
        if (!mismatch) {
            return "";
        }
        if (mismatch.actualCount < mismatch.expectedCount) {
            return `You set ${mismatch.expectedCount} scenes, but only ${mismatch.actualCount} were created`;
        }
        return `${mismatch.actualCount} scenes were created instead of ${mismatch.expectedCount}`;
    }

    canExitFaceSwapScenes() {
        const checkEnabledScenes = (arr) => {
            let enabled = false;
            forEach(arr, (s) => {
                if (s.enabled) {
                    enabled = true;
                }
            });
            return enabled;
        };
        if (!this.json || !this.json.admin || !this.json.admin.ai.faceSwap.faceSwapScenes) {
            return;
        }
        if (this.json.admin.ai.faceSwap.faceSwapScenes &&
            this.json.admin.ai.faceSwap.faceSwapScenes.enabled &&
            (!this.json.admin.ai.faceSwap.faceSwapScenes.scenesList.length || !checkEnabledScenes(this.json.admin.ai.faceSwap.faceSwapScenes.scenesList))) {
            return false;
        }
        let s = this.json.admin.ai.faceSwap.faceSwapScenes;

        if (!s.sceneLayoutAspectRatio) return false;
        if (!s.layoutHeight) return false;
        if (!s.layoutWidth) return false;
        if (!s.photos ||
            !s.photos[0].width ||
            !s.photos[0].height ||
            (!s.photos[0].x && s.photos[0].x !== 0) ||
            (!s.photos[0].y && s.photos[0].y !== 0)) {
            return false;
        }

        // s.photos = scenes.photos;
        return true;
    }

    canExitFaceSwapOverlays() {
        const checkEnabledScenes = (arr) => {
            let enabled = false;
            forEach(arr, (s) => {
                if (s.enabled) {
                    enabled = true;
                }
            });
            return enabled;
        };

        if (!this.json || !this.json.admin || !this.json.admin.ai.faceSwap.faceSwapOverlays) {
            return;
        }
        if (this.json.admin.ai.faceSwap.faceSwapOverlays &&
            this.json.admin.ai.faceSwap.faceSwapOverlays.enabled &&
            (!this.json.admin.ai.faceSwap.faceSwapOverlays.scenesList.length || !checkEnabledScenes(this.json.admin.ai.faceSwap.faceSwapOverlays.scenesList))) {
            return false;
        }
        let s = this.json.admin.ai.faceSwap.faceSwapOverlays;

        if (!s.sceneLayoutAspectRatio) return false;
        if (!s.layoutHeight) return false;
        if (!s.layoutWidth) return false;
        if (!s.photos ||
            !s.photos[0].width ||
            !s.photos[0].height ||
            (!s.photos[0].x && s.photos[0].x !== 0) ||
            (!s.photos[0].y && s.photos[0].y !== 0)) {
            return false;
        }

        // s.photos = scenes.photos;
        return true;
    }

    /**
     * Check if user can exit start screen
     * @returns {boolean}
     */
    canExitStartScreen() {
        let p = this.json && this.json.admin && this.json.admin.startScreenAreas ? this.json.admin.startScreenAreas : false;

        if (!p) {
            return false;
        }

        if (this.json.admin.photoBooth &&
            !this.json.admin.photoBooth.enabled &&
            this.json.admin.gifBooth &&
            !this.json.admin.gifBooth.enabled &&
            this.json.admin.boomerangBooth &&
            !this.json.admin.boomerangBooth.enabled &&
            this.json.admin.videoBooth &&
            !this.json.admin.videoBooth.enabled) {

            return false;
        }

        if (
            (p.single &&
                (!p.single.width || !p.single.height || typeof p.single.x !== "number" || typeof p.single.y !== "number") &&
                this.json.admin.photoBooth.enabled) ||
            (this.json.admin.photoBooth && this.json.admin.photoBooth.enabled && !p.single)
        ) {
            return false;
        }

        if (
            (p.gif &&
                (!p.gif.width || !p.gif.height || typeof p.gif.x !== "number" || typeof p.gif.y !== "number") &&
                this.json.admin.gifBooth.enabled) ||
            (this.json.admin.gifBooth && this.json.admin.gifBooth.enabled && !p.gif)
        ) {
            return false;
        }

        if (
            (p.boomerang &&
                (!p.boomerang.width || !p.boomerang.height || typeof p.boomerang.x !== "number" || typeof p.boomerang.y !== "number") &&
                this.json.admin.boomerangBooth.enabled) ||
            (this.json.admin.boomerangBooth && this.json.admin.boomerangBooth.enabled && !p.boomerang)
        ) {
            return false;
        }

        return true;
    }

    canExitCustomButtons() {
        let p = this.json && this.json.admin && this.json.admin.liveView.customButtons ? this.json.admin.liveView.customButtons : false;

        if (!p) {
            return false;
        }

        if (
            (p.generalUiButtonsSettings.back &&
                (!p.generalUiButtonsSettings.back.width || !p.generalUiButtonsSettings.back.height || typeof p.generalUiButtonsSettings.back.x !== "number" || typeof p.generalUiButtonsSettings.back.y !== "number"))) {
            return false;
        }

        if (
            (p.generalUiButtonsSettings.sharingBackBtn &&
                (!p.generalUiButtonsSettings.sharingBackBtn.width || !p.generalUiButtonsSettings.sharingBackBtn.height || typeof p.generalUiButtonsSettings.sharingBackBtn.x !== "number" || typeof p.generalUiButtonsSettings.sharingBackBtn.y !== "number"))) {
            return false;
        }

        if (
            (p.generalUiButtonsSettings.capture &&
                (!p.generalUiButtonsSettings.capture.width || !p.generalUiButtonsSettings.capture.height || typeof p.generalUiButtonsSettings.capture.x !== "number" || typeof p.generalUiButtonsSettings.capture.y !== "number"))) {
            return false;
        }

        if (
            (p.generalUiButtonsSettings.proceed &&
                (!p.generalUiButtonsSettings.proceed.width || !p.generalUiButtonsSettings.proceed.height || typeof p.generalUiButtonsSettings.proceed.x !== "number" || typeof p.generalUiButtonsSettings.proceed.y !== "number"))) {
            return false;
        }

        if (
            (p.generalUiButtonsSettings.next &&
                (!p.generalUiButtonsSettings.next.width || !p.generalUiButtonsSettings.next.height || typeof p.generalUiButtonsSettings.next.x !== "number" || typeof p.generalUiButtonsSettings.next.y !== "number"))) {
            return false;
        }

        if (
            (p.generalUiButtonsSettings.close &&
                (!p.generalUiButtonsSettings.close.width || !p.generalUiButtonsSettings.close.height || typeof p.generalUiButtonsSettings.close.x !== "number" || typeof p.generalUiButtonsSettings.close.y !== "number"))) {
            return false;
        }

        if (
            (p.generalUiButtonsSettings.closeError &&
                (!p.generalUiButtonsSettings.closeError.width || !p.generalUiButtonsSettings.closeError.height || typeof p.generalUiButtonsSettings.closeError.x !== "number" || typeof p.generalUiButtonsSettings.closeError.y !== "number"))) {
            return false;
        }

        if (
            (p.generalUiButtonsSettings.email &&
                (!p.generalUiButtonsSettings.email.width || !p.generalUiButtonsSettings.email.height || typeof p.generalUiButtonsSettings.email.x !== "number" || typeof p.generalUiButtonsSettings.email.y !== "number"))) {
            return false;
        }
        if (
            (p.generalUiButtonsSettings.sms &&
                (!p.generalUiButtonsSettings.sms.width || !p.generalUiButtonsSettings.sms.height || typeof p.generalUiButtonsSettings.sms.x !== "number" || typeof p.generalUiButtonsSettings.sms.y !== "number"))) {
            return false;
        }
        if (
            (p.generalUiButtonsSettings.print &&
                (!p.generalUiButtonsSettings.print.width || !p.generalUiButtonsSettings.print.height || typeof p.generalUiButtonsSettings.print.x !== "number" || typeof p.generalUiButtonsSettings.print.y !== "number"))) {
            return false;
        }
        if (
            (p.generalUiButtonsSettings.done &&
                (!p.generalUiButtonsSettings.done.width || !p.generalUiButtonsSettings.done.height || typeof p.generalUiButtonsSettings.done.x !== "number" || typeof p.generalUiButtonsSettings.done.y !== "number"))) {
            return false;
        }
        if (
            (p.generalUiButtonsSettings.qrCode &&
                (!p.generalUiButtonsSettings.qrCode.width || !p.generalUiButtonsSettings.qrCode.height || typeof p.generalUiButtonsSettings.qrCode.x !== "number" || typeof p.generalUiButtonsSettings.qrCode.y !== "number"))) {
            return false;
        }
        if (
            (p.captureModeButtonsSettings.single &&
                (!p.captureModeButtonsSettings.single.width || !p.captureModeButtonsSettings.single.height || typeof p.captureModeButtonsSettings.single.x !== "number" || typeof p.captureModeButtonsSettings.single.y !== "number"))) {
            return false;
        }
        if (
            (p.captureModeButtonsSettings.gif &&
                (!p.captureModeButtonsSettings.gif.width || !p.captureModeButtonsSettings.gif.height || typeof p.captureModeButtonsSettings.gif.x !== "number" || typeof p.captureModeButtonsSettings.gif.y !== "number"))) {
            return false;
        }
        if (
            (p.captureModeButtonsSettings.boomerang &&
                (!p.captureModeButtonsSettings.boomerang.width || !p.captureModeButtonsSettings.boomerang.height || typeof p.captureModeButtonsSettings.boomerang.x !== "number" || typeof p.captureModeButtonsSettings.boomerang.y !== "number"))) {
            return false;
        }
        if (
            (p.captureModeButtonsSettings.scene &&
                (!p.captureModeButtonsSettings.scene.width || !p.captureModeButtonsSettings.scene.height || typeof p.captureModeButtonsSettings.scene.x !== "number" || typeof p.captureModeButtonsSettings.scene.y !== "number"))) {
            return false;
        }
        if (
            (p.captureModeButtonsSettings.filter &&
                (!p.captureModeButtonsSettings.filter.width || !p.captureModeButtonsSettings.filter.height || typeof p.captureModeButtonsSettings.filter.x !== "number" || typeof p.captureModeButtonsSettings.filter.y !== "number"))) {
            return false;
        }

        return true;
    }

    onIconChange() {
        if (this.animatedIcons) {
            let p = this.json && this.json.admin && this.json.admin.startScreenAreas ? this.json.admin.startScreenAreas : false;

            if (!p) {
                return false;
            }

            for (let key in p) {
                if (p.hasOwnProperty(key) && p[key].width && p[key].height) {
                    p[key].width = Math.min(p[key].width, p[key].height);
                    p[key].height = Math.min(p[key].width, p[key].height);
                }
            }
        }
    }

    /**
     * Format/DPI change: pixels from getPixelsForFormat; reset layout to one frame on canvas;
     * if strip — activatePhotoStrips. Called from md-select format and DPI.
     */
    changeSinglePhotoLayoutRatio(value) {
        let config = this.viewValue[value];
        const formatId = config.layoutAspectRatio;
        const dpi = config.printDpi != null ? config.printDpi : 300;
        const p = getPixelsForFormat(formatId, dpi);
        config.printCanvasWidth = p.width;
        config.printCanvasHeight = p.height;

        config.photosArr = [{
            x: 0,
            y: 0,
            width: p.width,
            height: p.height,
            angle: 0,
            title: "1",
            image: true
        }];
        config.strippedPhotosArr = [];
        if (config.strip) {
            this.activatePhotoStrips(value);
        }
        this.changePhotosAspectRatio(value);
    }

    /** Preview scale: longest canvas side ≈ 400px on screen; DPI must not shrink the preview. */
    getPrintPreviewIndex(typ) {
        const config = this.viewValue[typ];
        if (!config || config.printCanvasWidth == null || config.printCanvasHeight == null) {
            return 4.5;
        }
        const targetDisplayMax = 400;
        const currentMax = Math.max(config.printCanvasWidth, config.printCanvasHeight);
        if (currentMax <= 0) {
            return 4.5;
        }
        return currentMax / targetDisplayMax;
    }

    /** DPI for html-preview (bleed); fallback 300. */
    getPrintDpi(typ) {
        const config = this.viewValue[typ];
        return (config && config.printDpi != null) ? config.printDpi : 300;
    }

    // Only when new configuration loaded
    setPhotosForPrint(value) {
        let config = this.viewValue[value];
        let gifPhotosQuantity = [1, 2, 3];
        let photosDefaultPosY = [82, 577, 1072];
        config.photosArr = [];
        config.strippedPhotosArr = [];
        config.digitalFormat.photos = [];
        switch (value) {
            case "singlePhoto":
                // Use printCanvasWidth/Height (from format); fallback 1252×1843 (portrait 300dpi)
                config.photosArr.push({
                    width: config.printCanvasWidth || 1252,
                    height: config.printCanvasHeight || 1843,
                    x: 0,
                    y: 0,
                    angle: 0,
                    title: "1",
                    image: true
                });
                break;
            case "gif":
            case "boomerang":
                forEach(gifPhotosQuantity, (gif) => {
                    config.photosArr.push({
                        width: 480,
                        height: 480,
                        x: 80,
                        y: photosDefaultPosY[gif - 1],
                        angle: 0,
                        title: gif + "",
                        image: true
                    });
                });
                break;
            case "video":
                forEach(gifPhotosQuantity, (gif) => {
                    config.photosArr.push({
                        width: 480,
                        height: 480,
                        x: 80,
                        y: photosDefaultPosY[gif - 1],
                        angle: 0,
                        title: gif + "",
                        image: true
                    });
                });
                break;
        }
        config.digitalFormat.photos = [{
            width: 500,
            height: 500,
            y: 0,
            x: 0,
            angle: 0,
            video: value === "boomerang",
            trueVideo: value === "video",
            gif: value === "gif",
            duration: this.json && this.json.admin && this.json.admin.gifBooth.playbackSpeed ? this.json.admin.gifBooth.playbackSpeed : 2,
            image: value === "singlePhoto"
        }];
    }

    changePhotosAspectRatio(value = "singlePhoto", isClick) {
        let config = this.viewValue[value],
            widthIndex, heightIndex;
        forEach(config.photosArr, (ph) => {
            ph.storedWidth = ph.width;
            ph.storedHeight = ph.height;
            ph.width = getSquare(ph.width, ph.height);
            ph.height = getSquare(ph.width, ph.height);
        });
        forEach(config.strippedPhotosArr, (ph) => {
            ph.storedWidth = ph.width;
            ph.storedHeight = ph.height;
            ph.width = getSquare(ph.width, ph.height);
            ph.height = getSquare(ph.width, ph.height);
        });
        forEach(config.digitalFormat.photos, (ph) => {
            ph.storedWidth = ph.width;
            ph.storedHeight = ph.height;
            ph.width = getSquare(ph.width, ph.height);
            ph.height = getSquare(ph.width, ph.height);
        });

        switch (config.photoCaptureRatio) {
            case "square":
                widthIndex = 1;
                heightIndex = 1;
                config.customPhotoHeight = null;
                config.customPhotoWidth = null;
                break;
            case "landscape":
                widthIndex = 3;
                heightIndex = 2;
                config.customPhotoHeight = null;
                config.customPhotoWidth = null;
                break;
            case "portrait":
                widthIndex = 2;
                heightIndex = 3;
                config.customPhotoHeight = null;
                config.customPhotoWidth = null;
                break;
            case "custom":
                if (isClick || config.customPhotoHeight === 0 || config.customPhotoWidth === 0 || config.customPhotoWidth < 0 || config.customPhotoHeight < 0) {
                    config.customPhotoWidth = 1;
                    config.customPhotoHeight = 1;
                }
                if (config.customPhotoHeight / config.customPhotoWidth >= 0.1 && config.customPhotoHeight / config.customPhotoWidth <= 10) {
                    widthIndex = config.customPhotoWidth;
                    heightIndex = config.customPhotoHeight;
                }
                if (!isClick) {
                    widthIndex = widthIndex || 1;
                    heightIndex = heightIndex || 1;
                }
        }

        if (widthIndex && heightIndex) {
            if (widthIndex >= heightIndex) {
                forEach(config.photosArr, (ph) => {
                    ph.height = Math.round((heightIndex * ph.width) / widthIndex);
                    this.photoTransform.centrePhoto(ph);
                });
                forEach(config.strippedPhotosArr, (ph) => {
                    ph.height = Math.round((heightIndex * ph.width) / widthIndex);
                    this.photoTransform.centrePhoto(ph);
                });
                forEach(config.digitalFormat.photos, (ph) => {
                    ph.height = Math.round((heightIndex * ph.width) / widthIndex);
                    this.photoTransform.centrePhoto(ph);
                });
            } else {
                forEach(config.photosArr, (ph) => {
                    ph.width = Math.round((widthIndex * ph.height) / heightIndex);
                    this.photoTransform.centrePhoto(ph);
                });
                forEach(config.strippedPhotosArr, (ph) => {
                    ph.width = Math.round((widthIndex * ph.height) / heightIndex);
                    this.photoTransform.centrePhoto(ph);
                });
                forEach(config.digitalFormat.photos, (ph) => {
                    ph.width = Math.round((widthIndex * ph.height) / heightIndex);
                    this.photoTransform.centrePhoto(ph);
                });
            }
            // if (isClick) {
            //     config.strippedPhotosArr = this.photoTransform.removeOverlap(config.strippedPhotosArr, config.layoutAspectRatio);
            //     config.photosArr = this.photoTransform.removeOverlap(config.photosArr, config.layoutAspectRatio);
            //     config.digitalFormat.photos = this.photoTransform.removeOverlap(config.digitalFormat.photos, config.layoutAspectRatio);
            //     }
        }

        function getSquare(a, b) {
            return Math.max(a, b);
        }

    }

    changeLayoutSize() {
        let aspectRation, width, height;
        const gcd = (a, b) => (b === 0) ? a : gcd(b, a % b);
        width = this.json.admin.general.width;
        height = this.json.admin.general.height;
        aspectRation = gcd(width, height);

        this.json.admin.general.aspectRatio.width = width / aspectRation;
        this.json.admin.general.aspectRatio.height = height / aspectRation;

        if (this.json.admin.liveView.liveViewMode === "fullscreen") this.updateViewMode();
    }

    changeLayoutRatio(value = "singlePhoto", isClick) {
        let config = this.viewValue[value].digitalFormat,
            widthIndex, heightIndex;
        config.layoutHeight = 500;
        config.layoutWidth = 500;
        config.photos[0].changed = Math.random();
        switch (config.layoutAspectRatio) {
            case "square":
                config.layoutHeight = 500;
                config.layoutWidth = 500;
                config.customPhotoWidth = null;
                config.customPhotoHeight = null;
                break;
            case "landscape":
                config.layoutHeight = 333;
                config.layoutWidth = 500;
                config.customPhotoWidth = null;
                config.customPhotoHeight = null;
                break;
            case "portrait":
                config.layoutHeight = 500;
                config.layoutWidth = 333;
                config.customPhotoWidth = null;
                config.customPhotoHeight = null;
                break;
            case "custom":
                if (isClick || config.customPhotoHeight === 0 || config.customPhotoWidth === 0 || config.customPhotoWidth < 0 || config.customPhotoHeight < 0) {
                    config.customPhotoWidth = 1;
                    config.customPhotoHeight = 1;
                }
                if (config.customPhotoHeight / config.customPhotoWidth >= 0.1 && config.customPhotoHeight / config.customPhotoWidth <= 10) {
                    widthIndex = config.customPhotoWidth;
                    heightIndex = config.customPhotoHeight;
                }
        }

        if (widthIndex && heightIndex) {
            if (widthIndex > heightIndex) {
                config.layoutHeight = Math.round((heightIndex * config.layoutWidth) / widthIndex);
            } else {
                config.layoutWidth = Math.round((widthIndex * config.layoutHeight) / heightIndex);
            }
        }

        if (!config.photos[0].width || !config.photos[0].height) {
            Object.assign(config.photos[0], {width: config.layoutWidth, height: config.layoutHeight});
        }
        let ph = config.photos[0];
        ph.x = 0;
        ph.y = 0;
        let maxSide = Math.min(config.layoutHeight, config.layoutWidth);

        if (ph.width >= ph.height) {
            ph.width = maxSide;
            this.changePhotosSize(ph, value, "width");
        } else if (ph.width < ph.height) {
            ph.height = maxSide;
            this.changePhotosSize(ph, value, "height");
        }
    }

    changePhotosSize(item, setting = "singlePhoto", value) {
        let config = setting ? this.viewValue[setting] : null,
            widthIndex, heightIndex;

        widthIndex = 1;
        heightIndex = 1;
        if (config) {
            switch (config.photoCaptureRatio) {
                case "square":
                    break;
                case "landscape":
                    widthIndex = 3;
                    heightIndex = 2;
                    break;
                case "portrait":
                    widthIndex = 2;
                    heightIndex = 3;
                    break;
                case "custom":
                    if (widthIndex && heightIndex) {
                        widthIndex = config.customPhotoWidth;
                        heightIndex = config.customPhotoHeight;
                    }
                    break;
            }
        }

        if (value === "height") {
            item.width = Math.round((item.height * widthIndex) / heightIndex);
        } else {
            item.height = Math.round((item.width * heightIndex) / widthIndex);
        }
    }

    changeSharingLayoutSize(setting, value) {
        let config = this.viewValue[setting],
            widthIndex, heightIndex;
        let round2 = (num) => this.Math.ceil(num / 2) * 2;

        widthIndex = 1;
        heightIndex = 1;
        switch (config.digitalFormat.layoutAspectRatio) {
            case "square":
                break;
            case "landscape":
                widthIndex = 3;
                heightIndex = 2;
                break;
            case "portrait":
                widthIndex = 2;
                heightIndex = 3;
                break;
            case "custom":
                if (widthIndex && heightIndex) {
                    widthIndex = config.digitalFormat.customPhotoWidth;
                    heightIndex = config.digitalFormat.customPhotoHeight;
                }
                break;
        }
        if (value === "height") {
            config.digitalFormat.layoutWidth = Math.round((config.digitalFormat.layoutHeight * widthIndex) / heightIndex);
            config.digitalFormat.layoutWidth = round2(config.digitalFormat.layoutWidth);
        } else {
            if (config.digitalFormat.layoutWidth){
                config.digitalFormat.layoutHeight = Math.round((config.digitalFormat.layoutWidth * heightIndex) / widthIndex);
                config.digitalFormat.layoutHeight = round2(config.digitalFormat.layoutHeight);
            }else{
                config.digitalFormat.layoutHeight = Math.round((this.json.admin.general.width * heightIndex) / widthIndex);
                config.digitalFormat.layoutHeight = round2(this.json.admin.general.height);
            }
        }
        let ph = config.digitalFormat.photos[0];
        ph.x = 0;
        ph.y = 0;
        let maxSide = Math.min((config.digitalFormat.layoutHeight ? config.digitalFormat.layoutHeight : this.json.admin.general.height),
                                        (config.digitalFormat.layoutWidth ? config.digitalFormat.layoutWidth : this.json.admin.general.width));

        if (ph.width >= ph.height) {
            ph.width = maxSide;
            ph.changed = Math.random();
            this.changePhotosSize(ph, setting, "width");
        } else if (ph.width < ph.height) {
            ph.height = maxSide;
            ph.changed = Math.random();
            this.changePhotosSize(ph, setting, "height");
        }
    }

    addNewPhoto(value = "singlePhoto", side) {
        let config = this.viewValue[value];
        let lastPhoto = side === "a" ? config.photosArr[config.photosArr.length - 1] : config.strippedPhotosArr[config.strippedPhotosArr.length - 1];

        let newPhoto = {
            width: lastPhoto.width,
            height: lastPhoto.height,
            x: lastPhoto.x,
            y: lastPhoto.y,
            angle: lastPhoto.angle,
            title: getName(side === "a" ? config.photosArr : config.strippedPhotosArr),
            image: lastPhoto.image,
            video: lastPhoto.video,
            trueVideo: lastPhoto.trueVideo,
        };
        if (config.layoutAspectRatio === "portrait" || config.layoutAspectRatio === "portrait_3_4") {
            newPhoto.y += lastPhoto.height + this.photoTransform.getMargin(side === "a" ? config.photosArr : config.strippedPhotosArr, "portrait");
            if (newPhoto.y + newPhoto.height > config.printCanvasHeight) {
                this.showErrorDialog("Error", "You can't add photo outside the printing area!<br><br>Move or change size of existing photo(s)");
                return;
            }

        } else {
            newPhoto.x += lastPhoto.width + this.photoTransform.getMargin(side === "a" ? config.photosArr : config.strippedPhotosArr, "landscape");
            if (newPhoto.x + newPhoto.width > config.printCanvasWidth) {
                this.showErrorDialog("Error", "You can't add photo outside the printing area!<br><br>Move or change size of existing photo(s)");
                return;
            }
        }
        side === "a" ? config.photosArr.push(newPhoto) : config.strippedPhotosArr.push(newPhoto);

        function getName(arr) {
            let editedArr = [];
            let title;
            forEach(arr, (ph) => {
                editedArr.push(Number(ph.title.replace("a", "").replace("b", "")));
            });

            for (let i = 1; i <= editedArr.length; i++) {
                if (editedArr.indexOf(i) === -1) {
                    title = config.strip ? i + side : i + "";
                }
            }

            if (!title) {
                title = config.strip ? editedArr[editedArr.length - 1] + 1 + side : editedArr[editedArr.length - 1] + 1 + "";
            }

            return title;
        }
    }

    removePhoto(photo, value = "singlePhoto", side) {
        let config = this.viewValue[value];
        side === "a" ? config.photosArr.splice(photo, 1) : config.strippedPhotosArr.splice(photo, 1);
    }

    resetPhotoArr() {
        let config = this.viewValue["singlePhoto"];
        while (config.photosArr.length > 1) {
            config.photosArr.pop();
        }
    }

    /**
     * Photo Strips: isLandscape = (width > height); previously layoutAspectRatio === "landscape".
     * Universal for A1–A6, 10×15, 15×20: portrait — split by width, landscape — by height.
     */
    activatePhotoStrips(value = "singlePhoto") {
        let config = this.viewValue[value];
        if (config.strip) {
            const isLandscape = (config.printCanvasWidth || 0) > (config.printCanvasHeight || 0);
            if (isLandscape) {
                // Landscape: split by height (two rows)
                forEach(config.photosArr, (ph) => {
                    if (ph.height > config.printCanvasHeight / 2) {
                        ph.height = config.printCanvasHeight / 2 - 142;
                        ph.y += 80;
                    }
                    this.changePhotosSize(ph, value, "height");
                    ph.height + ph.y > config.printCanvasHeight / 2 ? ph.y = ph.y - config.printCanvasHeight / 2 : null;
                    ph.y < 0 ? ph.y = 0 : null;
                    ph.title += "a";
                    ph.image = true;
                });

                // Add new photos
                let stripedArr = [];
                forEach(config.photosArr, (ph) => {
                    stripedArr.push({
                        width: ph.width,
                        height: ph.height,
                        x: ph.x,
                        y: config.printCanvasHeight - ph.y - ph.height,
                        angle: ph.angle,
                        title: ph.title.replace("a", "b"),
                        image: true
                    });
                });
                //config.photosArr = [...config.photosArr, ...stripedArr];
                config.strippedPhotosArr = stripedArr;
            } else {
                // Portrait: split by width (two columns)
                forEach(config.photosArr, (ph) => {
                    if (ph.width > config.printCanvasWidth / 2) {
                        ph.width = config.printCanvasWidth / 2 - 142;
                        ph.x += 80;
                    }
                    this.changePhotosSize(ph, value, "width");
                    ph.width + ph.x > config.printCanvasWidth / 2 ? ph.x = ph.x - config.printCanvasWidth / 2 : null;
                    ph.x < 0 ? ph.x = 0 : null;
                    ph.title += "a";
                    ph.image = true;
                });

                // Add new photos
                let stripedArr = [];
                forEach(config.photosArr, (ph) => {
                    stripedArr.push({
                        width: ph.width,
                        height: ph.height,
                        x: config.printCanvasWidth - ph.x - ph.width,
                        y: ph.y,
                        angle: ph.angle,
                        title: ph.title.replace("a", "b"),
                        image: true
                    });
                });
                config.strippedPhotosArr = stripedArr;
            }
        } else {
            config.photosArr = filter(config.photosArr, (ph) => {
                return ph.title.indexOf("b") === -1;
            });
            forEach(config.photosArr, (ph) => {
                ph.title = ph.title.replace("a", "");
            });
            config.strippedPhotosArr = [];
        }
    }

    mergeArrays(arr1, arr2) {
        return [...arr1, ...arr2];
    }

    showMessage() {
        !this.canExitSinglePhoto() && this.$mdDialog.show(
            this.$mdDialog.alert()
                .clickOutsideToClose(true)
                .title(this.gettext("Error"))
                .htmlContent(this.gettext("Your choices are incompatibile. You must either:<br><br>- Allow printing or sharing digital Single Photos (or both), or<br>- Disable Single Photos in this configuration"))
                .ariaLabel(this.gettext("Error"))
                .ok("OK")
                .targetEvent(event)
        );
    }

    showErrorDialog(title, HTMLtext) {
        this.$mdDialog.show(
            this.$mdDialog.alert()
                .clickOutsideToClose(true)
                .title(this.gettext(title))
                .htmlContent(this.gettext(HTMLtext))
                .ariaLabel(this.gettext("Error"))
                .ok("OK")
                .targetEvent(event)
        );
    }

    saveConfiguration(exit) {
        if (!this.json) return false;

        if (!this.canExitForms()) {
            this.getErrorItems(this.formOfflineForms.$error.required);
            this.showErrorDialog("Error", "Enter all required inputs");
            return false;
        }
        if (this.json.admin.ai.faceSwap.active) {
            Object.assign(this.config.json.admin.general.aspectRatio,
                {
                    width: this.toViewValue.detectAspectRatioObject(this.config.json.admin.general.width, this.config.json.admin.general.height).w,
                    height: this.toViewValue.detectAspectRatioObject(this.config.json.admin.general.width, this.config.json.admin.general.height).h
                });
        }
        const transformValue = {
            general: this.config.json.admin.general,
            liveView: this.config.json.admin.liveView
        };
        this.filterForms();
        this.config.json = angular.copy(this.json);
        this.config.json.admin.photoBooth.sharingFile = this.toBoothValue.transformSharingPhoto("singlePhoto", this.viewValue, transformValue, !this.json.admin.ai.faceSwap.active);
        this.config.json.admin.photoBooth.aspectRatio = this.toBoothValue.transformPhotoAspectRatio("singlePhoto", this.viewValue);
        this.config.json.admin.photoBooth.printFile = this.toBoothValue.transformPrintingPhotos("singlePhoto", this.viewValue);

        this.config.json.admin.gifBooth.sharingFile = this.toBoothValue.transformSharingPhoto("gif", this.viewValue, transformValue, !this.json.admin.ai.faceSwap.active);
        this.config.json.admin.gifBooth.aspectRatio = this.toBoothValue.transformPhotoAspectRatio("gif", this.viewValue);
        this.config.json.admin.gifBooth.printFile = this.toBoothValue.transformPrintingPhotos("gif", this.viewValue);

        this.config.json.admin.boomerangBooth.sharingFile = this.toBoothValue.transformSharingPhoto("boomerang", this.viewValue, transformValue, !this.json.admin.ai.faceSwap.active);
        this.config.json.admin.boomerangBooth.aspectRatio = this.toBoothValue.transformPhotoAspectRatio("boomerang", this.viewValue);
        this.config.json.admin.boomerangBooth.printFile = this.toBoothValue.transformPrintingPhotos("boomerang", this.viewValue);

        this.config.json.admin.startScreenDefaultIcons = this.animatedIcons;
        if (this.config.json.client.emailTextNoHtml) {
            this.config.json.client.emailTextNoHtml = this.emailTemplates.addLineBreaks(this.config.json.client.emailTextNoHtml);
        }
        this.config.json.admin.startScreenAreas = this.toBoothValue.transformStartScreenAreas(this.config.json.admin.startScreenAreas);
        if (this.config.json.admin.startScreenLogo) {
            this.config.json.admin.startScreenLogo.boothStyle = this.toBoothValue.setLogoStyle(this.config.json.admin.startScreenLogo);
        }
        this.config.json.admin.scenes = this.toBoothValue.transformScenes(this.config.json.admin.scenes);
        this.config.json.admin.ai.faceSwap.faceSwapScenes = this.toBoothValue.transformScenes(this.config.json.admin.ai.faceSwap.faceSwapScenes, false, {
            height: this.json.admin.general.height,
            width: this.json.admin.general.width
        });
        Object.assign(this.json.admin.ai.faceSwap.faceSwapScenes, {
            layoutHeight: this.json.admin.general.height,
            layoutWidth: this.json.admin.general.width
        });
        this.config.json.admin.ai.faceSwap.faceSwapOverlays = this.toBoothValue.transformScenes(this.config.json.admin.ai.faceSwap.faceSwapOverlays, false, {
            height: this.json.admin.general.height,
            width: this.json.admin.general.width
        });
        Object.assign(this.json.admin.ai.faceSwap.faceSwapOverlays, {
            layoutHeight: this.json.admin.general.height,
            layoutWidth: this.json.admin.general.width
        });

        this.createTemplateFromDefault();
        // Compared as sent (like $DetectChanges): keys left undefined, e.g. by addPropertiesFromServer, are not changes
        const changed = !isEqual(JSON.parse(angular.toJson(this.copiedConfig.json)), JSON.parse(angular.toJson(this.config.json)));
        if (changed || exit) {
            this.loading = true;
            this.$mdToast.show(this.loadingTost);
            this.config.json.admin.photoBooth.updatePrint =
                this.detectChanges.checkBoothPrintChange(this.config.json.admin.photoBooth, this.copiedConfig.json.admin.photoBooth, this.config.json.admin.startScreenLogo.file, this.copiedConfig.json.admin.startScreenLogo.file);
            this.config.json.admin.photoBooth.updateSharing =
                this.detectChanges.checkBoothSharingChange(this.config.json.admin.photoBooth, this.copiedConfig.json.admin.photoBooth, this.config.json.admin.startScreenLogo.file, this.copiedConfig.json.admin.startScreenLogo.file);

            this.config.json.admin.gifBooth.updatePrint =
                this.detectChanges.checkBoothPrintChange(this.config.json.admin.gifBooth, this.copiedConfig.json.admin.gifBooth, this.config.json.admin.startScreenLogo.file, this.copiedConfig.json.admin.startScreenLogo.file);
            this.config.json.admin.gifBooth.updateSharing =
                this.detectChanges.checkBoothSharingChange(this.config.json.admin.gifBooth, this.copiedConfig.json.admin.gifBooth, this.config.json.admin.startScreenLogo.file, this.copiedConfig.json.admin.startScreenLogo.file);

            this.config.json.admin.boomerangBooth.updatePrint =
                this.detectChanges.checkBoothPrintChange(this.config.json.admin.boomerangBooth, this.copiedConfig.json.admin.boomerangBooth, this.config.json.admin.startScreenLogo.file, this.copiedConfig.json.admin.startScreenLogo.file);
            this.config.json.admin.boomerangBooth.updateSharing =
                this.detectChanges.checkBoothSharingChange(this.config.json.admin.boomerangBooth, this.copiedConfig.json.admin.boomerangBooth, this.config.json.admin.startScreenLogo.file, this.copiedConfig.json.admin.startScreenLogo.file);

            this.config.json.admin.scenes = this.detectChanges.detectScenesChanges(this.config.json.admin.scenes, this.copiedConfig.json.admin.scenes);
            this.config.json.admin.ai.faceSwap.faceSwapScenes = this.detectChanges.detectScenesChanges(this.config.json.admin.ai.faceSwap.faceSwapScenes, this.copiedConfig.json.admin.ai.faceSwap.faceSwapScenes);
            Object.assign(this.json.admin.ai.faceSwap.faceSwapScenes, {
                layoutHeight: this.json.admin.general.height,
                layoutWidth: this.json.admin.general.width
            });
            this.config.json.admin.ai.faceSwap.faceSwapOverlays = this.detectChanges.detectScenesChanges(this.config.json.admin.ai.faceSwap.faceSwapOverlays, this.copiedConfig.json.admin.ai.faceSwap.faceSwapOverlays);
            Object.assign(this.json.admin.ai.faceSwap.faceSwapOverlays, {
                layoutHeight: this.json.admin.general.height,
                layoutWidth: this.json.admin.general.width
            });

            // Remove "Extra large print file" feature: do not send in config; old app: undefined = false
            delete this.config.json.admin.extraLargePrintFile;

            this.$Configuration.update(this.config)
                .$promise
                .then((resp) => {
                    delete resp["us_active"];
                    delete resp["us_api_key"];
                    if (exit) {
                        this.toastService.showSimple("Configuration saved!", 5000);
                        this.$state.go("configurations");
                    }
                    this.addPropertiesFromServer(resp);
                    this.deleteUpdatedProperties();
                    this.copiedConfig = cloneDeep(this.config);
                    if (this.json.client && this.json.client.emailTextNoHtml) {
                        this.config.json.client.emailTextNoHtml = this.emailTemplates.removeLineBreaks(this.config.json.client.emailTextNoHtml);
                    }
                    this.loading = false;
                    this.$mdToast.hide(this.loadingTost);
                }).catch((error) => {
                this.showErrorDialog("Error", `<p>${error.data && error.data.error ? error.data.error : "Some error occured"}</p>`);
                this.$state.go("configurations");
            });
        }
    }

    getErrorItems(el) {
        this.formErrorFields = [];
        if (Array.isArray(el)) {
            el.forEach(e => {
                this.formErrorFields.push(e.$name);
                this.formOfflineForms[e.$name].$touched = true;
                this.formOfflineForms[e.$name].$dirty = true;
            });
        }
        return this.formErrorFields;
    }

    isErrorFiield(name) {
        return this.formErrorFields.includes(name);
    }

    deleteUpdatedProperties() {
        delete this.config.updated;
        if (this.config.json.admin.startScreenAreas) {
            delete this.config.json.admin.startScreenAreas.random;
        }

        delete this.config.json.admin.photoBooth.updatePrint;
        delete this.config.json.admin.photoBooth.updateSharing;

        delete this.config.json.admin.gifBooth.updatePrint;
        delete this.config.json.admin.gifBooth.updateSharing;

        delete this.config.json.admin.boomerangBooth.updatePrint;
        delete this.config.json.admin.boomerangBooth.updateSharing;

        if (this.config.json.admin.scenes && this.config.json.admin.scenes.scenesList && this.config.json.admin.scenes.scenesList.length) {
            forEach(this.config.json.admin.scenes.scenesList, (s) => {
                delete s.updateScene;
            });
        }
        if (this.config.json.admin.ai.faceSwap.faceSwapScenes && this.config.json.admin.ai.faceSwap.faceSwapScenes.scenesList && this.config.json.admin.ai.faceSwap.faceSwapScenes.scenesList.length) {
            forEach(this.config.json.admin.ai.faceSwap.faceSwapScenes.scenesList, (s) => {
                delete s.updateScene;
            });
        }
        if (this.config.json.admin.ai.faceSwap.faceSwapOverlays && this.config.json.admin.ai.faceSwap.faceSwapOverlays.scenesList && this.config.json.admin.ai.faceSwap.faceSwapOverlays.scenesList.length) {
            forEach(this.config.json.admin.ai.faceSwap.faceSwapOverlays.scenesList, (s) => {
                delete s.updateScene;
            });
        }
    }

    addPropertiesFromServer(resp) {

        this.json.admin.photoBooth.printOverlay = cloneDeep(resp.json.admin.photoBooth.printOverlay);
        this.json.admin.photoBooth.printBackground = cloneDeep(resp.json.admin.photoBooth.printBackground);
        this.json.admin.photoBooth.sharingOverlay = cloneDeep(resp.json.admin.photoBooth.sharingOverlay);

        this.json.admin.photoBooth.sharingBackground = cloneDeep(resp.json.admin.photoBooth.sharingBackground);
        this.json.admin.gifBooth.printOverlay = cloneDeep(resp.json.admin.gifBooth.printOverlay);

        this.json.admin.gifBooth.printBackground = cloneDeep(resp.json.admin.gifBooth.printBackground);
        this.json.admin.gifBooth.sharingOverlay = cloneDeep(resp.json.admin.gifBooth.sharingOverlay);
        this.json.admin.gifBooth.sharingBackground = cloneDeep(resp.json.admin.gifBooth.sharingBackground);

        this.json.admin.boomerangBooth.printOverlay = cloneDeep(resp.json.admin.boomerangBooth.printOverlay);
        this.json.admin.boomerangBooth.printBackground = cloneDeep(resp.json.admin.boomerangBooth.printBackground);

        this.json.admin.boomerangBooth.sharingOverlay = cloneDeep(resp.json.admin.boomerangBooth.sharingOverlay);
        this.json.admin.boomerangBooth.sharingBackground = cloneDeep(resp.json.admin.boomerangBooth.sharingBackground);

        if (resp.json.admin.scenes && this.json.admin.scenes) {
            forEach(resp.json.admin.scenes.scenesList, (scene, i) => {
                this.json.admin.scenes.scenesList[i].finalBackground = scene.finalBackground;
                this.json.admin.scenes.scenesList[i].finalOverlay = scene.finalOverlay;
            });
        }
        if (resp.json.admin.ai.faceSwap.faceSwapScenes && this.json.admin.ai.faceSwap.faceSwapScenes) {
            forEach(resp.json.admin.ai.faceSwap.faceSwapScenes.scenesList, (scene, i) => {
                this.json.admin.ai.faceSwap.faceSwapScenes.scenesList[i].finalBackground = scene.finalBackground;
                this.json.admin.ai.faceSwap.faceSwapScenes.scenesList[i].finalOverlay = scene.finalOverlay;
            });
        }
        if (resp.json.admin.ai.faceSwap.faceSwapOverlays && this.json.admin.ai.faceSwap.faceSwapOverlays) {
            forEach(resp.json.admin.ai.faceSwap.faceSwapOverlays.scenesList, (scene, i) => {
                this.json.admin.ai.faceSwap.faceSwapOverlays.scenesList[i].finalBackground = scene.finalBackground;
                this.json.admin.ai.faceSwap.faceSwapOverlays.scenesList[i].finalOverlay = scene.finalOverlay;
            });
        }

        this.json.admin.liveView.customButtons = resp.json.admin.liveView.customButtons;
    }

    getFileType(file) {
        if (!file) {
            return true;
        }

        if (!file.attachment_file) {
            return true;
        }

        if (typeof file.attachment_file !== "string") {
            return true;
        }

        let str = file.attachment_file.split(".");

        return str[str.length - 1];
    }

    changeGifDuration() {
        this.viewValue.gif.digitalFormat.photos[0].duration = this.json.admin.gifBooth.playbackSpeed;
    }

    disableEmailFields() {
        if (!this.json || !this.json.admin || !this.json.admin.outputSettings) {
            return false;
        }
        return !this.json.admin.outputSettings.enableEmailSharing;
    }

    disableSmsFields() {
        if (!this.json || !this.json.admin || !this.json.admin.outputSettings) {
            return false;
        }
        return !this.json.admin.outputSettings.enableSmsSharing;
    }

    hidePrintFields() {
        if (!this.json || !this.json.admin) {
            return false;
        }
        let hide = false;
        if ((!this.json.admin.photoBooth.enabled || !this.json.admin.photoBooth.allowPrinting) &&
            (!this.json.admin.gifBooth.enabled || !this.json.admin.gifBooth.allowPrinting) &&
            (!this.json.admin.boomerangBooth.enabled || !this.json.admin.boomerangBooth.allowPrinting)) {
            hide = true;
        }
        return hide;
    }

    hasPrintingInEnabledBooth() {
        if (!this.json || !this.json.admin) {
            return false;
        }
        const booths = ["photoBooth", "gifBooth", "boomerangBooth"];
        return booths.some(booth =>
            this.json.admin[booth] && this.json.admin[booth].enabled && this.json.admin[booth].allowPrinting
        );
    }

    onFormConfigurationChange(input) {
        if ([...this.json.admin.offlineForms.formInputs,
            ...this.json.admin.offlineForms.customInputs,
            ...this.json.admin.offlineForms.formSignature,
            ...this.json.admin.offlineForms.formCheckboxes
        ].find(item => item.active) || !this.json.admin.offlineForms.enabled) {
            return true;
        } else {
            const confirm = this.$mdDialog.confirm()
                .title(`No active inputs left`)
                .textContent("Do you want to disable User Consent?")
                .ariaLabel("dialog window")
                .ok("Confirm")
                .cancel("Cancel");
            this.$mdDialog.show(confirm).then(() => {
                this.json.admin.offlineForms.enabled = false;
            }, () => {
                if (input) {
                    input.active = true;
                } else {
                    this.json.admin.offlineForms.formInputs[0].active = true;
                }
            });
        }
    }

    addChoiceField(customFieldIndex) {
        this.json.admin.offlineForms.customInputs[customFieldIndex].choices.push({text: ""});
    }

    removeChoiceField(customFieldIndex, choiceIndex) {
        this.json.admin.offlineForms.customInputs[customFieldIndex].choices.splice(choiceIndex, 1);
    }

    isCustomTitleUnique(title, ci) {
        return this.json.admin.offlineForms.customInputs.some((input, i) => input.title === title && ci !== i);
    }

    onCountOfPrintsChange(printsCount) {
        printsCount < 2 ? this.json.admin.autoPrintCopies = 1 : false;
    }

    filterForms() {
        this.json.admin.offlineForms.isMultipleAnswers = !this.formsOneAnswer;
        this.json.admin.offlineForms.customInputs = this.json.admin.offlineForms.customInputs
            .map(input => ({
                ...input,
                choices: input.inputMode !== "multiple" ? [] : input.choices.filter(choice => choice.text)
            }));
    }

    // Email sharing code
    onAdvancedInputChange() {
        if (!this.json.client.advancedHtmlInput) {
            this.json.client.emailTemplate = "English";
            this.json.client.emailSubject = "Your UpReach photo";
        }
        this.onTemplateChange();
    }

    onTemplateChange() {
        switch (this.json.client.emailTemplate) {
            case "German informal":
                this.json.client.emailSubject = "Dein UpReach Foto";
                this.json.client.emailTitle = "Hey, super Foto!";
                this.json.client.emailTextNoHtml = "Bitte schön, dein tolles UpReach-Foto findest du im Anhang 🎉 Vielen Dank für deinen Besuch! 🙏\n" +
                    "Scrolle ans Ende der E-Mail, um das Foto herunterzuladen & zu teilen.";
                this.json.client.emailTextRow = this.emailTemplates.getTemplate("germanInformal", this.json.client.emailTitle, this.json.client.emailTextNoHtml);
                this.json.client.smsText = "Hey, super Foto! 🎉\n" +
                    "Besuche die Live-Galerie, um dein UpReach-Foto herunterzuladen & zu teilen: ";
                return;
            case "German formal":
                this.json.client.emailSubject = "Ihr UpReach Foto";
                this.json.client.emailTitle = "Hey, super Foto!";
                this.json.client.emailTextNoHtml = "Bitte sehr, Ihr tolles UpReach-Foto finden Sie im Anhang 🎉 Vielen Dank für Ihren Besuch! 🙏\n" +
                    "Scrollen Sie ans Ende der E-Mail, um das Foto herunterzuladen & zu teilen.";
                this.json.client.emailTextRow = this.emailTemplates.getTemplate("germanFormal", this.json.client.emailTitle, this.json.client.emailTextNoHtml);
                this.json.client.smsText = "Hey, super Foto! 🎉\n" +
                    "Besuchen Sie die Live-Galerie, um Ihr UpReach-Foto herunterzuladen & zu teilen: ";
                return;
            case "English":
                this.json.client.emailSubject = "Your UpReach photo";
                this.json.client.emailTitle = "Hey, nice snap!";
                this.json.client.emailTextNoHtml = "Here you are, please find attached your awesome UpReach photo. Thank you so much for visiting us!\n Scroll down to download & share your image.";
                this.json.client.emailTextRow = this.emailTemplates.getTemplate("english", this.json.client.emailTitle, this.json.client.emailTextNoHtml);
                this.json.client.smsText = "Hey, nice snap!\nVisit the live gallery to download & share your UpReach photo: ";
                return;
        }
    }

    createTemplateFromDefault() {
        this.config.json.client.emailText = cloneDeep(this.config.json.client.emailTextRow);
        if (!this.json.client.advancedHtmlInput) {
            let template = null;
            switch (this.json.client.emailTemplate) {
                case "German informal":
                    template = "germanInformal";
                    break;
                case "German formal":
                    template = "germanFormal";
                    break;
                case "English":
                    template = "english";
                    break;
            }
            if (template) {
                this.config.json.client.emailText = this.emailTemplates.getTemplate(template, this.config.json.client.emailTitle, this.config.json.client.emailTextNoHtml);
            }
        } else {
            this.config.json.client.emailText = this.emailTemplates.replaceItems(this.config.json.client.emailTextRow, this.json.client.emailTitle, this.json.client.emailTextNoHtml);
        }
    }

    testUpload(file, value, mode) {
        if (this.json && this.json.admin) {
            if (!this.json.admin[value][mode]) {
                this.json.admin[value][mode] = [];
            }
            if (mode.indexOf("printing") !== -1) {
                this.json.admin[value][mode][0] = file.attachment_file;
            } else {
                if (isArray(file)) {
                    let newArr = [];
                    forEach(file, (f) => {
                        newArr.push(f.attachment_file);
                    });
                    this.json.admin[value][mode].push(...newArr);
                } else {
                    this.json.admin[value][mode].push(file.attachment_file);
                    this.json.admin[value][mode + "ValidRatio"] = {width: file.width, height: file.height};
                }
            }
        }
    }

    gdprCustomButtonUpload(file, mode) {
        if (this.json && this.json.admin) {
            if (!this.json.admin["gdpr"][mode]) {
                this.json.admin["gdpr"][mode] = [];
            }
            if (isArray(file)) {
                let newArr = [];
                forEach(file, (f) => {
                    newArr.push(f.attachment_file);
                });
                this.json.admin["gdpr"][mode].push(...newArr);
            } else {
                this.json.admin["gdpr"][mode] = [];
                this.json.admin["gdpr"][mode].push(file);
            }
        }
    }

    pdfUpload(file, value, index) {
        if (this.json && this.json.admin) {
            if (!this.json.admin[value]["formCheckboxes"]) {
                this.json.admin[value]["formCheckboxes"] = [];
            }
            this.json.admin[value]["formCheckboxes"][index].fileUrl = file.attachment_file;
            this.json.admin[value]["formCheckboxes"][index].fileName = file.original_name;
            this.json.admin[value]["formCheckboxes"][index].usePdf = true;
            const urlParts = file.url.split("/");
            this.json.admin[value]["formCheckboxes"][index].fileId = urlParts[urlParts.length - 2];
            this.saveDataInConfig();
        }
    }

    pdfGdprUpload(file, mode) {
        if (this.json && this.json.admin) {
            if (!this.json.admin.gdpr[mode]) {
                this.json.admin.gdpr[mode] = [];
            }
            this.json.admin.gdpr[mode] = [];
            this.json.admin.gdpr[mode].push(file);
            const urlParts = file.url.split("/");
            this.json.admin.gdpr.fileId = urlParts[urlParts.length - 2];
            this.saveDataInConfig();
        }
    }

    customLogoUpload(file, mode) {
        if (this.json && this.json.admin) {
            if (!this.json.admin.ai["customLogo"][mode]) {
                this.json.admin.ai["customLogo"][mode] = [];
            }
            if (isArray(file)) {
                let newArr = [];
                forEach(file, (f) => {
                    newArr.push(f.attachment_file);
                });
                this.json.admin.ai["customLogo"][mode].push(...newArr);
            } else {
                this.json.admin.ai["customLogo"][mode] = [];
                this.json.admin.ai["customLogo"][mode].push(file);
            }
            this.saveConfiguration(false);
        }
    }

    pdfDeleteFile(fileId, value, index) {
        if (this.json && this.json.admin) {
            this.$upload.delete("/api/attachments/" + fileId + "/").then((resp) => {
                if (resp) {
                    this.json.admin[value]["formCheckboxes"][index].fileId = null;
                    this.json.admin[value]["formCheckboxes"][index].fileUrl = "";
                    this.json.admin[value]["formCheckboxes"][index].fileName = "";
                    this.toastService.showSimple("File id deleted!", 5000);
                    this.saveDataInConfig();
                }
            }, (error) => {
                if (fileId === null) {
                    this.showErrorDialog("Error", "<p>The file is missing!</p>");
                } else {
                    this.showErrorDialog("Error", `<p>${error.data && error.data.error ? error.data.error : "Cannot DELETE " + this.json.admin[value]["formCheckboxes"][index].fileName}</p>`);
                }
            });
        }
    }

    gdprDeleteFile(fileId, value) {
        if (this.json && this.json.admin) {
            this.$upload.delete("/api/attachments/" + fileId + "/").then((resp) => {
                if (resp) {
                    this.json.admin.gdpr[value] = [];
                    this.json.admin.gdpr.fileId = null;
                    this.toastService.showSimple("File id deleted!", 5000);
                    this.saveDataInConfig();
                }
            }, (error) => {
                if (fileId === null) {
                    this.showErrorDialog("Error", "<p>The file is missing!</p>");
                } else {
                    this.showErrorDialog("Error", `<p>${error.data && error.data.error ? error.data.error : "Cannot DELETE " + this.json.admin.gdpr[value][0].fileName}</p>`);
                }
            });
        }
    }

    deleteFile(index, value, mode, isGdpr = false) {
        if (this.json && this.json.admin) {
            this.json.admin[value][mode].splice(index, 1);
            if (this.json.admin[value][mode].length === 0) {
                this.json.admin[value][mode] = [];
                if (!isGdpr) {
                    this.json.admin[value][mode + "ValidRatio"] = null;
                } else {
                    this.saveDataInConfig();
                }
            }
        }
    }

    customLogoDeleteFile(index, value, mode) {
        if (this.json && this.json.admin) {
            this.json.admin.ai[value][mode].splice(index, 1);
            if (this.json.admin.ai[value][mode].length === 0) {
                this.json.admin.ai[value][mode] = [];
            }
            this.saveConfiguration(false);
        }
    }

    checkStartScreenDesign(booth) {
        this.json.admin[booth].printingBackgroundMode = "image";
        this.json.admin[booth].printingBackgroundColor = "#ffffff";
        //this.json.admin[booth].printingBackgroundFiles = [];

        if (this.json.admin[booth].startScreenDesignSharing) {
            this.json.admin[booth].sharingBackgroundMode = this.json.admin.startScreenBackgroundMode;
            this.json.admin[booth].sharingBackgroundColor = this.json.admin.startScreenBackgroundColor;
            if (this.json.admin[booth].sharingBackgroundMode === "image" && this.json.admin.startScreenBackground) {
                this.json.admin[booth].sharingBackgroundFiles = [(this.json.admin.startScreenBackground.first_frame || this.json.admin.startScreenBackground.attachment_file)];
            }
        } else {
            this.json.admin[booth].sharingBackgroundMode = "image";
            this.json.admin[booth].sharingBackgroundColor = "#ffffff";
            //this.json.admin[booth].sharingBackgroundFiles = [];
        }
    }

    checkDesignForAllModes() {
        this.checkStartScreenDesign("photoBooth");
        this.checkStartScreenDesign("gifBooth");
        this.checkStartScreenDesign("boomerangBooth");
    }

    checkBackgroundResolution() {
        setTimeout(() => {
            if (this.json.admin && this.json.admin.startScreenBackground && this.json.admin.startScreenBackground.attachment_file) {
                let isVideo = this.json.admin.startScreenBackground.attachment_file.indexOf(".mp4") !== -1;
                const VALID_RESOLUTION = this.json.admin.startScreenPreviewSize.width / this.json.admin.startScreenPreviewSize.height;
                let media = isVideo ? new Video() : new Image();
                media.src = this.json.admin.startScreenBackground.attachment_file;
                if (isVideo) {
                    media.onloadeddata = () => {
                        if (media.videoWidth / media.videoHeight !== VALID_RESOLUTION) {
                            this.showBgToggle = true;
                        } else {
                            this.showBgToggle = false;
                        }
                    };
                } else {
                    media.onload = () => {
                        if (media.width / media.height !== VALID_RESOLUTION) {
                            this.showBgToggle = true;
                        } else {
                            this.showBgToggle = false;
                        }
                    };
                }
            } else {
                this.showBgToggle = false;
            }
        });

        function Video() {
            return document.createElement("video");
        }
    }

    changeAreas() {
        if (this.json.admin.startScreenLogo.position <= 3) {
            (this.json.admin.startScreenAreas.single.y && this.json.admin.startScreenAreas.single.y <= 50) ? this.json.admin.startScreenAreas.single.y = (100 - this.json.admin.startScreenAreas.single.y) : null;
            (this.json.admin.startScreenAreas.gif.y && this.json.admin.startScreenAreas.gif.y <= 50) ? this.json.admin.startScreenAreas.gif.y = (100 - this.json.admin.startScreenAreas.gif.y) : null;
            (this.json.admin.startScreenAreas.boomerang.y && this.json.admin.startScreenAreas.boomerang.y <= 50) ? this.json.admin.startScreenAreas.boomerang.y = (100 - this.json.admin.startScreenAreas.boomerang.y) : null;
            (this.json.admin.startScreenAreas.video.y && this.json.admin.startScreenAreas.video.y <= 50) ? this.json.admin.startScreenAreas.video.y = (100 - this.json.admin.startScreenAreas.video.y) : null;
        } else {
            (this.json.admin.startScreenAreas.single.y && this.json.admin.startScreenAreas.single.y > 50) ? this.json.admin.startScreenAreas.single.y = (100 - this.json.admin.startScreenAreas.single.y) : null;
            (this.json.admin.startScreenAreas.gif.y && this.json.admin.startScreenAreas.gif.y > 50) ? this.json.admin.startScreenAreas.gif.y = (100 - this.json.admin.startScreenAreas.gif.y) : null;
            (this.json.admin.startScreenAreas.boomerang.y && this.json.admin.startScreenAreas.boomerang.y > 50) ? this.json.admin.startScreenAreas.boomerang.y = (100 - this.json.admin.startScreenAreas.boomerang.y) : null;
            (this.json.admin.startScreenAreas.video.y && this.json.admin.startScreenAreas.video.y > 50) ? this.json.admin.startScreenAreas.video.y = (100 - this.json.admin.startScreenAreas.video.y) : null;
        }
    }

    // Fullscreen mode sets the scene ratios to "custom" without a size, saved as layoutAspectRatio/pictureAspectRatio {}.
    // Copied to Single Photo, GIF and Boomerang, such a ratio does not let the wizard leave them: take the size from the scene.
    fillSceneCustomRatios(scenes) {
        if (scenes.sceneLayoutAspectRatio === "custom" && (!scenes.customLayoutWidth || !scenes.customLayoutHeight)) {
            const layout = this.toViewValue.detectAspectRatioObject(Math.round(scenes.layoutWidth), Math.round(scenes.layoutHeight));
            scenes.customLayoutWidth = layout.w;
            scenes.customLayoutHeight = layout.h;
        }
        const photo = scenes.photos && scenes.photos[0];
        if (photo && scenes.photoAspectRatio === "custom" && (!scenes.customPhotoWidth || !scenes.customPhotoHeight)) {
            const ratio = this.toViewValue.detectAspectRatioObject(Math.round(photo.width), Math.round(photo.height));
            scenes.customPhotoWidth = ratio.w;
            scenes.customPhotoHeight = ratio.h;
        }
    }

    changeScenes() {
        if (!this.json) {
            return;
        }
        if (!this.json.admin.ai.faceSwap.active) {
            if ((this.json.admin.scenes && this.json.admin.scenes.enabled && this.json.admin.scenes.scenesList.length) || this.json.admin.liveView.liveViewMode === "fullscreen") {
                let sc = this.json.admin.scenes;
                this.fillSceneCustomRatios(sc);
                // Set single photo
                this.viewValue.singlePhoto.digitalFormat.customPhotoHeight = sc.customLayoutHeight;
                this.viewValue.singlePhoto.digitalFormat.customPhotoWidth = sc.customLayoutWidth;
                this.viewValue.singlePhoto.digitalFormat.layoutAspectRatio = sc.sceneLayoutAspectRatio;
                this.viewValue.singlePhoto.digitalFormat.layoutHeight = sc.layoutHeight;
                this.viewValue.singlePhoto.digitalFormat.layoutWidth = sc.layoutWidth;
                if (this.json.admin.scenes.enabled) {
                    this.viewValue.singlePhoto.photoCaptureRatio = sc.photoAspectRatio;
                }
                if (this.json.admin.scenes.enabled) {
                    this.viewValue.singlePhoto.customPhotoHeight = sc.customPhotoHeight;
                    this.viewValue.singlePhoto.customPhotoWidth = sc.customPhotoWidth;
                }
                if (sc.scenesList.length > 0) {
                    this.viewValue.singlePhoto.digitalFormat.photos = [
                        {
                            width: sc.scenesList[0].photos[0].width,
                            height: sc.scenesList[0].photos[0].height,
                            x: sc.scenesList[0].photos[0].x,
                            y: sc.scenesList[0].photos[0].y,
                            angle: sc.scenesList[0].photos[0].angle,
                            image: true
                        }
                    ];
                }
                this.changePhotosAspectRatio("singlePhoto");

                //set gif
                this.viewValue.gif.digitalFormat.customPhotoHeight = sc.customLayoutHeight;
                this.viewValue.gif.digitalFormat.customPhotoWidth = sc.customLayoutWidth;
                this.viewValue.gif.digitalFormat.layoutAspectRatio = sc.sceneLayoutAspectRatio;
                this.viewValue.gif.digitalFormat.layoutHeight = sc.layoutHeight;
                this.viewValue.gif.digitalFormat.layoutWidth = sc.layoutWidth;
                this.viewValue.gif.photoCaptureRatio = sc.photoAspectRatio;
                this.viewValue.gif.customPhotoHeight = sc.customPhotoHeight;
                this.viewValue.gif.customPhotoWidth = sc.customPhotoWidth;
                if (sc.scenesList.length > 0) {
                    this.viewValue.gif.digitalFormat.photos = [
                        {
                            width: sc.scenesList[0].photos[0].width,
                            height: sc.scenesList[0].photos[0].height,
                            x: sc.scenesList[0].photos[0].x,
                            y: sc.scenesList[0].photos[0].y,
                            angle: sc.scenesList[0].photos[0].angle,
                            gif: true
                        }
                    ];
                }
                this.changePhotosAspectRatio("gif");

                //set boomerang
                this.viewValue.boomerang.digitalFormat.customPhotoHeight = sc.customLayoutHeight;
                this.viewValue.boomerang.digitalFormat.customPhotoWidth = sc.customLayoutWidth;
                this.viewValue.boomerang.digitalFormat.layoutAspectRatio = sc.sceneLayoutAspectRatio;
                this.viewValue.boomerang.digitalFormat.layoutHeight = sc.layoutHeight;
                this.viewValue.boomerang.digitalFormat.layoutWidth = sc.layoutWidth;
                this.viewValue.boomerang.photoCaptureRatio = sc.photoAspectRatio;
                this.viewValue.boomerang.customPhotoHeight = sc.customPhotoHeight;
                this.viewValue.boomerang.customPhotoWidth = sc.customPhotoWidth;
                if (sc.scenesList.length > 0) {
                    this.viewValue.boomerang.digitalFormat.photos = [
                        {
                            width: sc.scenesList[0].photos[0].width,
                            height: sc.scenesList[0].photos[0].height,
                            x: sc.scenesList[0].photos[0].x,
                            y: sc.scenesList[0].photos[0].y,
                            angle: sc.scenesList[0].photos[0].angle,
                            video: true
                        }
                    ];
                }
                this.changePhotosAspectRatio("boomerang");

                //set video
                this.viewValue.video.digitalFormat.customPhotoHeight = sc.customLayoutHeight;
                this.viewValue.video.digitalFormat.customPhotoWidth = sc.customLayoutWidth;
                this.viewValue.video.digitalFormat.layoutAspectRatio = sc.sceneLayoutAspectRatio;
                this.viewValue.video.digitalFormat.layoutHeight = sc.layoutHeight;
                this.viewValue.video.digitalFormat.layoutWidth = sc.layoutWidth;
                this.viewValue.video.photoCaptureRatio = sc.photoAspectRatio;
                this.viewValue.video.customPhotoHeight = sc.customPhotoHeight;
                this.viewValue.video.customPhotoWidth = sc.customPhotoWidth;
                if (sc.scenesList.length > 0) {
                    this.viewValue.video.digitalFormat.photos = [
                        {
                            width: sc.scenesList[0].photos[0].width,
                            height: sc.scenesList[0].photos[0].height,
                            x: sc.scenesList[0].photos[0].x,
                            y: sc.scenesList[0].photos[0].y,
                            angle: sc.scenesList[0].photos[0].angle,
                            trueVideo: true
                        }
                    ];
                }
                this.changePhotosAspectRatio("video");

                if (this.json.admin.scenes.enabled) {
                    if (sc.scenesList.length > 0) {
                        this.setOverlaysAndBackgroundFilesOnScenes(sc.scenesList[0]);
                    }
                } else {
                    if (this.json.admin.photoBooth.savedData === undefined) {
                        const savedData = {
                            sharingOverlayFiles: "",
                            sharingBackgroundFiles: ""
                        };
                        Object.assign(this.json.admin.photoBooth, {savedData: savedData});
                    }
                    if (this.json.admin.gifBooth.savedData === undefined) {
                        const savedData = {
                            sharingOverlayFiles: "",
                            sharingBackgroundFiles: ""
                        };
                        Object.assign(this.json.admin.gifBooth, {savedData: savedData});
                    }
                    if (this.json.admin.boomerangBooth.savedData === undefined) {
                        const savedData = {
                            sharingOverlayFiles: "",
                            sharingBackgroundFiles: ""
                        };
                        Object.assign(this.json.admin.boomerangBooth, {savedData: savedData});
                    }
                    Object.assign(this.json.admin.photoBooth.savedData, {
                        sharingOverlayFiles: this.json.admin.photoBooth.sharingOverlayFiles,
                        sharingBackgroundFiles: this.json.admin.photoBooth.sharingBackgroundFiles
                    });
                    Object.assign(this.json.admin.gifBooth.savedData, {
                        sharingOverlayFiles: this.json.admin.gifBooth.sharingOverlayFiles,
                        sharingBackgroundFiles: this.json.admin.gifBooth.sharingBackgroundFiles
                    });
                    Object.assign(this.json.admin.boomerangBooth.savedData, {
                        sharingOverlayFiles: this.json.admin.boomerangBooth.sharingOverlayFiles,
                        sharingBackgroundFiles: this.json.admin.boomerangBooth.sharingBackgroundFiles
                    });
                }

                this.json.admin.photoBooth.allowSharing = true;
                this.json.admin.gifBooth.allowSharing = true;
                this.json.admin.boomerangBooth.allowSharing = true;
            }
        }
    }

    setOverlaysAndBackgroundFilesOnScenes(firstScene) {
        const getFiles = (arr) => {
            if (!arr || !arr.length || !arr[0] || !arr[0].hasOwnProperty("attachment_file")) return [];
            return arr.map((file) => file.attachment_file_alpha || file.attachment_file);
        };

        const getFirstFrameFiles = (arr) => {
            if (arr && arr.length >= 1) {
                if (arr[0] !== null && !arr[0].hasOwnProperty("first_frame")) {
                    return null;
                }

                if (arr[0].first_frame !== null) {
                    return arr.reduce((newString, file) => newString || file.first_frame_alpha || file.first_frame, null);
                } else {
                    return arr.reduce((newString, file) => newString || file.attachment_file_alpha || file.attachment_file, null);
                }
            } else {
                return null;
            }
        };
        this.json.admin.photoBooth.sharingBackgroundFiles = getFiles([firstScene.finalBackground]);
        this.json.admin.photoBooth.sharingOverlayFiles = getFiles([firstScene.overlayFiles]);
        this.json.admin.gifBooth.sharingBackgroundFiles = getFiles([firstScene.finalBackground]);
        this.json.admin.gifBooth.sharingOverlayFiles = getFiles([firstScene.overlayFiles]);
        this.json.admin.boomerangBooth.sharingBackgroundFiles = getFiles([firstScene.finalBackground]);
        this.json.admin.boomerangBooth.sharingOverlayFiles = getFiles([firstScene.overlayFiles]);

        if (this.json.admin.photoBooth.printBackground && !this.json.admin.photoBooth.printBackground.attachment_file) {
            this.json.admin.photoBooth.printBackground.attachment_file = this.json.admin.photoBooth.printBackground.thumbnail_file;
        }

        const setPrintOverlay = (booth) => {
            if (booth.printOverlay === null) {
                return false;
            }
            if (booth.printOverlay) {
                booth.printOverlay.attachment_file = getFirstFrameFiles(firstScene.overlayFiles, "overlay");
                if (booth.printOverlay.attachment_file === null) {
                    Object.assign(booth.printOverlay, {attachment_file: booth.printOverlay.thumbnail_file});
                }
            } else {
                if (firstScene.overlayFiles[0] === undefined) {
                    Object.assign(booth, {printOverlay: null});
                } else {
                    Object.assign(booth, {printOverlay: firstScene.overlayFiles[0]});
                    if (booth.printOverlay.attachment_file.includes(".mp4" || ".gif")) {
                        Object.assign(booth.printOverlay, {attachment_file: booth.printOverlay.first_frame});
                    }
                }
            }
        };

        const setPrintBackground = (booth) => {
            if (booth.printBackground !== null && booth.printBackground !== undefined) {
                booth.printBackground.attachment_file = getFirstFrameFiles([firstScene.finalBackground ? firstScene.finalBackground : firstScene.backgroundFiles], "background");
                if (booth.printBackground.attachment_file === null) {
                    Object.assign(booth.printBackground, {attachment_file: booth.printBackground.thumbnail_file});
                }
            } else {
                Object.assign(booth, {printBackground: firstScene.finalBackground ? firstScene.finalBackground[0] : null});
            }
            if (booth.printBackground && booth.printBackground.attachment_file === null && booth.printBackground.thumbnail_file !== null) {
                booth.printBackground.attachment_file = booth.printBackground.thumbnail_file;
            }
        };

        setPrintBackground(this.json.admin.photoBooth);
        setPrintBackground(this.json.admin.gifBooth);
        setPrintBackground(this.json.admin.boomerangBooth);
        setPrintOverlay(this.json.admin.photoBooth);
        setPrintOverlay(this.json.admin.gifBooth);
        setPrintOverlay(this.json.admin.boomerangBooth);
    }

    onSceneDisable() {
        if (this.json.admin.scenes && !this.json.admin.scenes.enabled) {
            if (this.json.admin.photoBooth.savedData && this.json.admin.photoBooth.savedData.sharingOverlayFiles !== undefined) {
                this.json.admin.photoBooth.sharingOverlayFiles = this.json.admin.photoBooth.savedData.sharingOverlayFiles;
            }
            if (this.json.admin.gifBooth.savedData && this.json.admin.gifBooth.savedData.sharingOverlayFiles !== undefined) {
                this.json.admin.gifBooth.sharingOverlayFiles = this.json.admin.gifBooth.savedData.sharingOverlayFiles;
            }
            if (this.json.admin.boomerangBooth.savedData && this.json.admin.boomerangBooth.savedData.sharingOverlayFiles !== undefined) {
                this.json.admin.boomerangBooth.sharingOverlayFiles = this.json.admin.boomerangBooth.savedData.sharingOverlayFiles;
            }
            if (this.json.admin.photoBooth.savedData && this.json.admin.photoBooth.savedData.sharingBackgroundFiles !== undefined) {
                this.json.admin.photoBooth.sharingBackgroundFiles = this.json.admin.photoBooth.savedData.sharingBackgroundFiles;
            }
            if (this.json.admin.gifBooth.savedData && this.json.admin.gifBooth.savedData.sharingBackgroundFiles !== undefined) {
                this.json.admin.gifBooth.sharingBackgroundFiles = this.json.admin.gifBooth.savedData.sharingBackgroundFiles;
            }
            if (this.json.admin.boomerangBooth.savedData && this.json.admin.boomerangBooth.savedData.sharingBackgroundFiles !== undefined) {
                this.json.admin.boomerangBooth.sharingBackgroundFiles = this.json.admin.boomerangBooth.savedData.sharingBackgroundFiles;
            }
        }
    }

    /**
     * The add-on settings (remove.bg, Replicate, Remaker, Unscreen, aging, face swap) come from their own endpoints
     * after the page has copied the loaded configuration (copiedConfig). Makes the same change in that copy:
     * otherwise the next step change sees a difference nobody made and saves the configuration.
     */
    updateCopiedConfig(update) {
        if (this.copiedConfig && this.copiedConfig.json && this.copiedConfig.json.admin) {
            update(this.copiedConfig.json.admin);
        }
    }

    getRemoveBgData() {
        this.$service.get(`/api/configurations/${this.$stateParams.cid}/remove-bg/`)
            .then(resp => {
                this.removeBg = resp;
                this.removeBgIsConnectedState = resp.api_key === "" ? false : true;
                this.json.admin.removeBg = this.removeBg;
                this.updateCopiedConfig((admin) => {
                    admin.removeBg = cloneDeep(resp);
                });
            })
            .catch(err => {
                this.removeBgIsConnectedState = false;
                this.toastService.showError(err.statusText, 5000, "bottom right");
            });
    }

    /**
     * Merges replicate-remove-bg API response into config without clobbering prompting fields
     * that are loaded from /replicate-prompting/ when the remove-bg payload omits them.
     */
    applyReplicateRemoveBgResponseToState(resp) {
        if (!resp) {
            return;
        }
        this.replicate = resp;
        if (!this.json.admin.ai.replicate) {
            this.json.admin.ai.replicate = {};
        }
        const prev = cloneDeep(this.json.admin.ai.replicate);
        Object.assign(this.json.admin.ai.replicate, resp);
        const promptingKeys = ["replicate_prompting_active", "replicate_prompt_quality", "replicate_model"];
        promptingKeys.forEach((key) => {
            if (resp[key] === undefined && prev[key] !== undefined) {
                this.json.admin.ai.replicate[key] = prev[key];
            }
        });
        this.normalizeReplicatePromptingState(this.json.admin.ai.replicate, prev);
        this.replicateIsConnectedState = resp.replicate_api_key === "" ? false : true;
    }

    getReplicateData() {
        this.$service.get(`/api/configurations/${this.$stateParams.cid}/replicate-remove-bg/`)
            .then(resp => {
                this.applyReplicateRemoveBgResponseToState(resp);
                this.updateCopiedConfig((admin) => {
                    admin.ai.replicate = cloneDeep(this.json.admin.ai.replicate);
                });
            })
            .catch(err => {
                this.replicateIsConnectedState = false;
                this.toastService.showError(err.statusText, 5000, "bottom right");
            });
    }

    getReplicatePromptingData() {
        this.$service.get(`/api/configurations/${this.$stateParams.cid}/replicate-prompting/`)
            .then(resp => {
                if (!this.json.admin.ai.replicate) {
                    this.json.admin.ai.replicate = {};
                }
                this.json.admin.ai.replicate.replicate_prompting_active = resp.replicate_prompting_active;
                if (resp.replicate_api_key !== undefined && resp.replicate_api_key !== null) {
                    this.json.admin.ai.replicate.replicate_api_key = resp.replicate_api_key;
                }
                this.updateCopiedConfig((admin) => {
                    admin.ai.replicate = cloneDeep(this.json.admin.ai.replicate);
                });
            })
            .catch(err => {
                this.toastService.showError(err.statusText, 5000, "bottom right");
            });
    }

    getRemakerData() {
        this.$service.get(`/api/configurations/${this.$stateParams.cid}/remaker-remove-bg/`)
            .then(resp => {
                this.remaker = resp;
                this.remakerIsConnectedState = resp.remaker_api_key === "" ? false : true;
                this.json.admin.ai.remaker = this.remaker;
                this.updateCopiedConfig((admin) => {
                    admin.ai.remaker = cloneDeep(resp);
                });
            })
            .catch(err => {
                this.remakerIsConnectedState = false;
                this.toastService.showError(err.statusText, 5000, "bottom right");
            });
    }

    getUnscreenData() {
        this.$service.get(`/api/configurations/${this.$stateParams.cid}/unscreen/`)
            .then(resp => {
                this.unscreen = resp;
                this.unscreenIsConnectedState = resp.us_api_key === "" ? false : true;
                this.json.admin.unscreen = this.unscreen;
                this.updateCopiedConfig((admin) => {
                    admin.unscreen = cloneDeep(resp);
                });
            })
            .catch(err => {
                this.unscreenIsConnectedState = false;
                this.toastService.showError(err.statusText, 5000, "bottom right");
            });
    }

    getWondershareApiData() {
        this.$service.get(`/api/configurations/${this.$stateParams.cid}/wondershare-aging/`)
            .then(resp => {
                this.wandershareApi = resp;
                this.wandershareApiIsConnectedState = resp.aging_api_key === "" ? false : true;
                Object.assign(this.json.admin.ai.wandershareApi, resp);
                Object.assign(this.json.admin.ai.wandershareApi, {aging_age_slider_allowed: !resp.aging_age_slider_allowed});
                this.updateCopiedConfig((admin) => {
                    admin.ai.wandershareApi = cloneDeep(this.json.admin.ai.wandershareApi);
                });
            })
            .catch(err => {
                this.wandershareApiIsConnectedState = false;
                this.toastService.showError(err.statusText, 5000, "bottom right");
            });
    }

    getFaceSwapApiData() {
        this.$service.get(`/api/configurations/${this.$stateParams.cid}/face-swap/`)
            .then(resp => {
                //Object.assign(this.faceSwapApi,resp);
                Object.assign(this.json.admin.ai.faceSwap, resp);
                // Only the keys of the response: faceSwapScenes have another format in the copy
                this.updateCopiedConfig((admin) => {
                    Object.assign(admin.ai.faceSwap, cloneDeep(resp));
                });
                this.faceSwapApiIsConnectedState = resp.face_swap_api_key === "" ? false : true;
                //this.json.admin.ai.faceSwap = this.faceSwapApi;
            })
            .catch(err => {
                this.faceSwapApiIsConnectedState = false;
                this.toastService.showError(err.statusText, 5000, "bottom right");
            });
    }

    setRemoveBgData(data) {
        // Логіка removeBg працює незалежно від Replicate та Remaker
        if (data.active) {
            setTimeout(() => {
                this.$service.put(`/api/configurations/${this.$stateParams.cid}/remove-bg/`, data)
                    .then(resp => {
                        this.removeBg = resp;
                        this.json.admin.removeBg = this.removeBg;
                        this.removeBgIsConnectedState = resp.api_key === "" ? false : true;
                        this.saveDataInConfig();
                    })
                    .catch(err => {
                        this.removeBgIsConnectedState = false;
                        this.toastService.showError(err.active[0], 5000, "bottom right");
                    });
            }, 600);
        } else if (!data.active) {
            this.$service.put(`/api/configurations/${this.$stateParams.cid}/remove-bg/`, data)
                .then(resp => {
                    this.removeBg = resp;
                    this.json.admin.removeBg = this.removeBg;
                    this.removeBgIsConnectedState = resp.api_key === "" ? false : true;
                    this.saveDataInConfig();
                })
                .catch(err => {
                    this.removeBgIsConnectedState = false;
                    this.toastService.showError(err.active[0], 5000, "bottom right");
                });
        }
    }

    setReplicateData(data) {
        // Replicate працює незалежно від removeBg, але взаємовиключно з Remaker
        if (data.replicate_remove_bg_active && this.json.admin.ai.remaker && this.json.admin.ai.remaker.remaker_remove_bg_active) {
            // Вимикаємо Remaker, якщо вмикаємо Replicate
            this.json.admin.ai.remaker.remaker_remove_bg_active = false;
            this.setRemakerData({remaker_remove_bg_active: false});
        }

        if (data.replicate_remove_bg_active) {
            setTimeout(() => {
                this.$service.put(`/api/configurations/${this.$stateParams.cid}/replicate-remove-bg/`, data)
                    .then(resp => {
                        this.applyReplicateRemoveBgResponseToState(resp);
                        this.saveDataInConfig();
                    })
                    .catch(err => {
                        this.replicateIsConnectedState = false;
                        this.toastService.showError(err.replicate_remove_bg_active[0], 5000, "bottom right");
                    });
            }, 600);
        } else if (!data.replicate_remove_bg_active) {
            this.$service.put(`/api/configurations/${this.$stateParams.cid}/replicate-remove-bg/`, data)
                .then(resp => {
                    this.applyReplicateRemoveBgResponseToState(resp);
                    this.saveDataInConfig();
                })
                .catch(err => {
                    this.replicateIsConnectedState = false;
                    this.toastService.showError(err.replicate_remove_bg_active[0], 5000, "bottom right");
                });
        }
    }

    setRemakerData(data) {
        // Remaker працює незалежно від removeBg, але взаємовиключно з Replicate
        if (data.remaker_remove_bg_active && this.json.admin.ai.replicate && this.json.admin.ai.replicate.replicate_remove_bg_active) {
            // Вимикаємо Replicate, якщо вмикаємо Remaker
            this.json.admin.ai.replicate.replicate_remove_bg_active = false;
            this.setReplicateData({replicate_remove_bg_active: false});
        }

        if (data.remaker_remove_bg_active) {
            setTimeout(() => {
                this.$service.put(`/api/configurations/${this.$stateParams.cid}/remaker-remove-bg/`, data)
                    .then(resp => {
                        this.remaker = resp;
                        this.json.admin.ai.remaker = this.remaker;
                        this.remakerIsConnectedState = resp.remaker_api_key === "" ? false : true;
                        this.saveDataInConfig();
                    })
                    .catch(err => {
                        this.remakerIsConnectedState = false;
                        this.toastService.showError(err.remaker_remove_bg_active[0], 5000, "bottom right");
                    });
            }, 600);
        } else if (!data.remaker_remove_bg_active) {
            this.$service.put(`/api/configurations/${this.$stateParams.cid}/remaker-remove-bg/`, data)
                .then(resp => {
                    this.remaker = resp;
                    this.json.admin.ai.remaker = this.remaker;
                    this.remakerIsConnectedState = resp.remaker_api_key === "" ? false : true;
                    this.saveDataInConfig();
                })
                .catch(err => {
                    this.remakerIsConnectedState = false;
                    this.toastService.showError(err.remaker_remove_bg_active[0], 5000, "bottom right");
                });
        }
    }

    setKeyReplicateData(data) {
        this.$service.put(`/api/configurations/${this.$stateParams.cid}/replicate-remove-bg/`, data)
            .then(resp => {
                this.applyReplicateRemoveBgResponseToState(resp);
                this.saveDataInConfig();
                if (resp.replicate_api_key !== "") {
                    this.toastService.showSimple("API key was added successfully", 5000);
                } else {
                    this.toastService.showError("Enter API key", 5000, "bottom right");
                }
                this.connectReplicate();
            })
            .catch(err => {
                this.replicateIsConnectedState = false;
                if (err.status === 403) {
                    this.replicate = err.data;
                    this.toastService.showError("Forbidden", 5000, "bottom right");
                } else {
                    if (err.data && err.data.assigned) {
                        err.data.assigned.forEach(text => {
                            this.toastService.showError(text, 5000, "bottom right");
                        });
                    } else if (err.data && err.data.replicate_remove_bg_active) {
                        this.toastService.showError(err.data.replicate_remove_bg_active[0], 5000, "bottom right");
                    }
                }
            });
    }

    connectReplicate() {
        this.showReplicateApiKeyForm = !this.showReplicateApiKeyForm;
    }

    normalizeReplicatePromptingState(replicateConfig, fallbackConfig) {
        if (!replicateConfig) {
            return;
        }

        if (replicateConfig.replicate_prompting_active === undefined) {
            replicateConfig.replicate_prompting_active = false;
        }

        if (!REPLICATE_PROMPT_MODELS[replicateConfig.replicate_prompt_quality]) {
            if (fallbackConfig && REPLICATE_PROMPT_MODELS[fallbackConfig.replicate_prompt_quality]) {
                replicateConfig.replicate_prompt_quality = fallbackConfig.replicate_prompt_quality;
            } else {
                replicateConfig.replicate_prompt_quality = REPLICATE_PROMPT_DEFAULT_QUALITY;
            }
        }

        if (!replicateConfig.replicate_model) {
            replicateConfig.replicate_model = REPLICATE_PROMPT_MODELS[replicateConfig.replicate_prompt_quality];
        }
    }

    setReplicatePromptingQuality(quality) {
        if (!REPLICATE_PROMPT_MODELS[quality]) {
            return;
        }

        if (!this.json.admin.ai.replicate) {
            this.json.admin.ai.replicate = {};
        }

        // Optimistic UI update: backend may not echo custom prompting fields back
        this.json.admin.ai.replicate.replicate_prompting_active = true;
        this.json.admin.ai.replicate.replicate_prompt_quality = quality;
        this.json.admin.ai.replicate.replicate_model = REPLICATE_PROMPT_MODELS[quality];

        const payload = {
            replicate_prompting_active: true,
            replicate_prompt_quality: quality,
            replicate_model: REPLICATE_PROMPT_MODELS[quality]
        };

        if (this.json.admin.ai.replicate && this.json.admin.ai.replicate.replicate_remove_bg_active !== undefined) {
            payload.replicate_remove_bg_active = this.json.admin.ai.replicate.replicate_remove_bg_active;
        }

        this.setReplicateData(payload);
    }

    onReplicatePromptingToggle() {
        if (!this.json.admin.ai.replicate) {
            this.json.admin.ai.replicate = {};
        }

        const previousValue = !this.json.admin.ai.replicate.replicate_prompting_active;
        const currentValue = this.json.admin.ai.replicate.replicate_prompting_active;

        const payload = {
            replicate_prompting_active: currentValue
        };

        if (this.json.admin.ai.replicate.replicate_api_key !== undefined) {
            payload.replicate_api_key = this.json.admin.ai.replicate.replicate_api_key;
        }

        this.$service.put(`/api/configurations/${this.$stateParams.cid}/replicate-prompting/`, payload)
            .then(resp => {
                if (!this.json.admin.ai.replicate) {
                    this.json.admin.ai.replicate = {};
                }
                this.json.admin.ai.replicate.replicate_prompting_active = resp.replicate_prompting_active;
                if (resp.replicate_api_key !== undefined && resp.replicate_api_key !== null) {
                    this.json.admin.ai.replicate.replicate_api_key = resp.replicate_api_key;
                }
            })
            .catch(err => {
                this.json.admin.ai.replicate.replicate_prompting_active = previousValue;
                const errorMessage = err && err.data && err.data.replicate_prompting_active
                    ? err.data.replicate_prompting_active[0]
                    : err.statusText;
                this.toastService.showError(errorMessage, 5000, "bottom right");
            });
    }

    setKeyRemakerData(data) {
        this.$service.put(`/api/configurations/${this.$stateParams.cid}/remaker-remove-bg/`, data)
            .then(resp => {
                this.remaker = resp;
                this.json.admin.ai.remaker = this.remaker;
                this.remakerIsConnectedState = resp.remaker_api_key === "" ? false : true;
                this.saveDataInConfig();
                if (resp.remaker_api_key !== "") {
                    this.toastService.showSimple("API key was added successfully", 5000);
                } else {
                    this.toastService.showError("Enter API key", 5000, "bottom right");
                }
                this.connectRemaker();
            })
            .catch(err => {
                this.remakerIsConnectedState = false;
                if (err.status === 403) {
                    this.remaker = err.data;
                    this.toastService.showError("Forbidden", 5000, "bottom right");
                } else {
                    if (err.data && err.data.assigned) {
                        err.data.assigned.forEach(text => {
                            this.toastService.showError(text, 5000, "bottom right");
                        });
                    } else if (err.data && err.data.remaker_remove_bg_active) {
                        this.toastService.showError(err.data.remaker_remove_bg_active[0], 5000, "bottom right");
                    } else if (err.data && err.data.remaker_api_key) {
                        this.toastService.showError(err.data.remaker_api_key[0], 5000, "bottom right");
                    }
                }
            });
    }

    connectRemaker() {
        this.showRemakerApiKeyForm = !this.showRemakerApiKeyForm;
    }

    setUnscreenData(data) {
        this.$service.put(`/api/configurations/${this.$stateParams.cid}/unscreen/`, data)
            .then(resp => {
                this.unscreen = resp;
                this.json.admin.unscreen = this.unscreen;
                this.unscreenIsConnectedState = resp.us_api_key === "" ? false : true;
                this.saveDataInConfig();
            })
            .catch(err => {
                this.unscreenIsConnectedState = false;
                err.data.assigned.forEach(text => {
                    this.toastService.showError(text, 5000, "bottom right");
                });
            });
    }

    setWandershareApiData(data) {
        this.$service.put(`/api/configurations/${this.$stateParams.cid}/wondershare-aging/`, data)
            .then(resp => {
                this.wandershareApi = resp;
                //Object.assign(this.json.admin.ai.wandershareApi,resp);
                this.wandershareApiIsConnectedState = resp.aging_api_key === "" ? false : true;
                //this.saveDataInConfig();
            })
            .catch(err => {
                this.wandershareApiIsConnectedState = false;
                err.data.assigned.forEach(text => {
                    this.toastService.showError(text, 5000, "bottom right");
                });
            });
    }

    setFaceSwapApiData(data) {
        this.$service.put(`/api/configurations/${this.$stateParams.cid}/face-swap/`, data)
            .then(resp => {
                Object.assign(this.json.admin.ai.faceSwap, resp);
                this.json.admin.ai.faceSwap.faceSwapScenes.enabled = resp.face_swap_active;
                this.json.admin.ai.faceSwap.faceSwapOverlays.enabled = resp.face_swap_active;
                this.faceSwapApiIsConnectedState = resp.face_swap_api_key === "" ? false : true;
                this.saveDataInConfig();
            })
            .catch(err => {
                this.faceSwapApiIsConnectedState = false;
                err.data.assigned.forEach(text => {
                    this.toastService.showError(text, 5000, "bottom right");
                });
            });
    }

    onChangeMultiFaceswap(data){
        this.$service.put(`/api/configurations/${this.$stateParams.cid}/face-swap/`, {face_swap_multi_active:data})
            .then(resp => {
                this.json.admin.ai.faceSwap.face_swap_multi_active = resp.face_swap_multi_active;
                this.saveDataInConfig();
            })
            .catch(err => {
                this.faceSwapApiIsConnectedState = false;
                err.data.assigned.forEach(text => {
                    this.toastService.showError(text, 5000, "bottom right");
                });
            });
    }

    setLocalRemoveBgState(data) {
        if (data.remove_bg_offline_active) {
            setTimeout(() => {
                this.$service.put(`/api/configurations/${this.$stateParams.cid}/remove-bg-offline/`, data)
                    .then(() => {
                        this.saveDataInConfig();
                    })
                    .catch(err => {
                        this.toastService.showError(err.remove_bg_offline_active[0], 5000, "bottom right");
                    });
            }, 600);
        } else if (!data.remove_bg_offline_active) {
            this.$service.put(`/api/configurations/${this.$stateParams.cid}/remove-bg-offline/`, data)
                .then(resp => {
                    this.json.admin.removeBgOffline = resp;
                    this.saveDataInConfig();
                })
                .catch(err => {
                    this.toastService.showError(err.remove_bg_offline_active[0], 5000, "bottom right");
                });
        }
    }

    setKeyRemoveBgData(data) {
        this.$service.put(`/api/configurations/${this.$stateParams.cid}/remove-bg/`, data)
            .then(resp => {
                this.removeBg = resp;
                this.json.admin.removeBg = this.removeBg;
                this.removeBgIsConnectedState = resp.api_key === "" ? false : true;
                this.saveDataInConfig();
                if (resp.api_key !== "") {
                    this.toastService.showSimple("API key was added successfully", 5000);
                } else {
                    this.toastService.showError("Enter API key", 5000, "bottom right");
                }
                this.connectRemoveBg();
            })
            .catch(err => {
                this.removeBgIsConnectedState = false;
                if (err.status === 403) {
                    this.removeBg = err.data;
                    this.toastService.showError("Forbidden", 5000, "bottom right");
                } else {
                    err.data.assigned.forEach(text => {
                        this.toastService.showError(text, 5000, "bottom right");
                    });
                }
            });
    }

    setKeyWandershareApiData(data) {
        this.$service.put(`/api/configurations/${this.$stateParams.cid}/wondershare-aging/`, data)
            .then(resp => {
                this.wandershareApi = resp;
                //Object.assign(this.json.admin.ai.wandershareApi,resp);
                this.wandershareApiIsConnectedState = resp.aging_api_key === "" ? false : true;
                this.saveDataInConfig();
                if (resp.api_key !== "") {
                    this.toastService.showSimple("API key was added successfully", 5000);
                } else {
                    this.toastService.showError("Enter API key", 5000, "bottom right");
                }
                this.connectWandershareApi();
            })
            .catch(err => {
                this.wandershareApiIsConnectedState = false;
                if (err.status === 403) {
                    this.wandershareApi = err.data;
                    this.toastService.showError("Forbidden", 5000, "bottom right");
                } else {
                    if (err.data) {
                        err.data.assigned.forEach(text => {
                            this.toastService.showError(text, 5000, "bottom right");
                        });
                    }
                }
            });
    }

    setKeyFaceSwapApiData(data) {
        this.$service.put(`/api/configurations/${this.$stateParams.cid}/face-swap/`, data)
            .then(resp => {
                //Object.assign(this.faceSwapApi,resp);
                Object.assign(this.json.admin.ai.faceSwap, resp);
                this.faceSwapApiIsConnectedState = resp.face_swap_api_key === "" ? false : true;
                this.json.admin.ai.faceSwap.faceSwapScenes.enabled = resp.face_swap_active;
                this.json.admin.ai.faceSwap.faceSwapOverlays.enabled = resp.face_swap_active;
                this.saveDataInConfig();
                if (resp.face_swap_api_key !== "") {
                    this.toastService.showSimple("API key was added successfully", 5000);
                } else {
                    this.toastService.showError("Enter API key", 5000, "bottom right");
                }
                this.showFaceSwapApiKeyForm = true;
            })
            .catch(err => {
                this.faceSwapApiIsConnectedState = false;
                if (err.status === 403) {
                    Object.assign(this.json.admin.ai.faceSwap, err.data);
                    this.toastService.showError("Forbidden", 5000, "bottom right");
                } else {
                    if (err.data) {
                        err.data.assigned.forEach(text => {
                            this.toastService.showError(text, 5000, "bottom right");
                        });
                    }
                }
            });
    }

    connectRemoveBg() {
        this.showApiKeyForm = !this.showApiKeyForm;
    }

    connectUnscreen() {
        this.showUnscreenApiKeyForm = !this.showUnscreenApiKeyForm;
    }

    connectWandershareApi() {
        this.showWandershareApiKeyForm = !this.showWandershareApiKeyForm;
    }

    connectFaceSwapApi() {
        this.showFaceSwapApiKeyForm = !this.showFaceSwapApiKeyForm;
    }

    saveDataInConfig() {
        if (this.canExitSharingScreen()
            && this.canExitScreen("singlePhoto", "photoBooth")
            && this.canExitScreen("gif", "gifBooth")
            && this.canExitScreen("boomerang", "boomerangBooth")
            && this.canExitStartScreen()
            && this.canExitCustomButtons()
            && this.canExitScenes()
            && this.canExitFaceSwapScenes()
            && this.canExitFaceSwapOverlays()
            && this.canExitForms()) {
            this.checkDesignForAllModes();
            this.changeScenes();
            this.saveConfiguration(false);
        } else if (!this.loading) {
            this.canExitWizard();
        }
    }

    changeEmailState() {
        this.json.admin.offlineForms.formInputs[1].required = true;
    }

    changeStandByScreenState(state) {
        if (!state) this.json.admin.standbyScreenAfterEachSession = false;
    }

    ensureIndividualText() {
        if (!this.json || !this.json.admin) {
            return;
        }
        this.json.admin.individualText = normalizeIndividualText(this.json.admin.individualText,
            this.getIndividualTextScreenSize(), this.getSinglePhotoCanvasSize(), this.getIndividualTextOutput());
    }

    /**
     * The picture the texts go on, like the Scenes tab "Scene preview - Digital": with the scenes on,
     * the first scene on the Digital layout (the screen size in the fullscreen live view mode);
     * with the scenes off, the photo over the whole screen of the General tab.
     */
    getIndividualTextOutput() {
        const admin = this.json.admin;
        const general = admin.general || {};
        const scenes = admin.scenes || {};
        const getFiles = (files) => (files || []).filter(Boolean).map((file) => file.attachment_file_alpha || file.attachment_file);
        if (scenes.enabled) {
            // The Size of the Scenes tab, in fullscreen too: layoutWidth/Height while editing, finalSize as saved
            // (before the Scenes values are built)
            const layout = {
                width: scenes.layoutWidth || scenes.finalSize && scenes.finalSize.width,
                height: scenes.layoutHeight || scenes.finalSize && scenes.finalSize.height
            };
            // Before the first scene is created the size is still the scenes layout, as in "Scene preview - Digital"
            const scene = scenes.scenesList && scenes.scenesList[0] || {};
            return {
                width: layout.width || general.width,
                height: layout.height || general.height,
                // 3:2, 2:3 or 1:1 as named in the Scenes tab; a custom ratio is taken from the size
                ratio: scenes.sceneLayoutAspectRatio === "custom" ? null : this.toBoothValue.getSceneRatio(scenes.sceneLayoutAspectRatio),
                background: getFiles(scene.backgroundFiles),
                overlay: getFiles(scene.overlayFiles),
                areas: scenes.photos,
                animationTime: scene.animationTime
            };
        }
        return {
            width: general.width,
            height: general.height,
            background: [],
            overlay: [],
            areas: [{x: 0, y: 0, width: general.width, height: general.height, image: true}]
        };
    }

    /** "(9:16, 1080x1920px)" after a preview title; the ratio of the size unless given as {width, height}. */
    getIndividualTextSizeTitle(width, height, ratio) {
        width = Math.round(width);
        height = Math.round(height);
        if (!width || !height) {
            return "";
        }
        const r = ratio && ratio.width && ratio.height ? {w: ratio.width, h: ratio.height} : this.toViewValue.detectAspectRatioObject(width, height);
        return `(${r.w}:${r.h}, ${width}x${height}px)`;
    }

    /** The screen of the Screen preview has the size of the output picture: the Scenes Size with the scenes on, else the General tab. */
    getIndividualTextScreenSize() {
        const output = this.getIndividualTextOutput();
        return {width: output.width, height: output.height};
    }

    getIndividualTextScreenTitle() {
        const output = this.getIndividualTextOutput();
        return this.getIndividualTextSizeTitle(output.width, output.height, output.ratio);
    }

    getIndividualTextOutputTitle() {
        const output = this.getIndividualTextOutput();
        return this.getIndividualTextSizeTitle(output.width, output.height, output.ratio);
    }

    /** Single Photo print canvas (px) from the saved config, before viewValue is built; null when unknown. */
    getSinglePhotoCanvasSize() {
        try {
            const admin = this.json.admin;
            const size = getPixelsForFormat(this.toViewValue.getPrintLayoutRatio("photoBooth", admin),
                this.toViewValue.getPrintDpi("photoBooth", admin));
            return size && size.width && size.height ? size : null;
        } catch (error) {
            return null;
        }
    }

    isIndividualTextPortrait() {
        return this.json.admin.general.screenOrientation === "portrait";
    }

    /**
     * Places the two previews in the free space of the previews column (individualTextPreviewSpace):
     * side by side with one height, or one under the other sharing the height so both get about the same area.
     * Picks the variant where the smaller preview is bigger. Both fit the window height, so the sticky column stays visible.
     */
    getIndividualTextPreviewLayout() {
        const screen = this.getIndividualTextScreenSize();
        const space = this.individualTextPreviewSpace || {width: 500, height: 900};
        const screenRatio = screen.width / screen.height;
        const outputRatio = this.getIndividualTextOutputRatio() || 2 / 3;
        // Titles above a preview and the gap between previews, px
        const title = 80;
        const gap = 24;
        // The app header and the titles above the first row; a small window still gets readable previews
        const height = Math.max(360, space.height - 50 - title);
        const width = Math.max(300, space.width - 8);

        const rowHeight = Math.min(height, (width - gap) / (screenRatio + outputRatio));
        const row = {row: true, screenHeight: rowHeight, outputHeight: rowHeight};

        // One under the other: equal areas, then each no wider than the column, the freed height to the other one
        const columnHeight = height - title;
        let screenHeight = Math.min(columnHeight / (1 + Math.sqrt(screenRatio / outputRatio)), width / screenRatio);
        const outputHeight = Math.min(columnHeight - screenHeight, width / outputRatio);
        screenHeight = Math.min(columnHeight - outputHeight, width / screenRatio);
        const column = {row: false, screenHeight, outputHeight};

        const smallerArea = (layout) => Math.min(layout.screenHeight * layout.screenHeight * screenRatio,
            layout.outputHeight * layout.outputHeight * outputRatio);
        return smallerArea(row) >= smallerArea(column) ? row : column;
    }

    getIndividualTextScreenIndex() {
        return this.getIndividualTextScreenSize().height / this.getIndividualTextPreviewLayout().screenHeight;
    }

    /** The output picture on the screen keeps the proportions of the output picture. */
    getIndividualTextOutputRatio() {
        const output = this.getIndividualTextOutput();
        return output.width && output.height ? output.width / output.height : null;
    }

    /** Never smaller than the picture inside the screen preview. */
    getIndividualTextOutputIndex() {
        const output = this.getIndividualTextOutput();
        if (!output.height) {
            return 1;
        }
        const fit = output.height / this.getIndividualTextPreviewLayout().outputHeight;
        return Math.min(fit, this.getIndividualTextOutputIndexInScreen());
    }

    /** html-preview index that fits the output picture into the output box on the screen preview. */
    getIndividualTextOutputIndexInScreen() {
        const area = this.json.admin.individualText.outputPreview;
        const screenIndex = this.getIndividualTextScreenIndex();
        const screen = this.getIndividualTextScreenSize();
        const boxWidth = area.width / 100 * screen.width / screenIndex;
        const boxHeight = area.height / 100 * screen.height / screenIndex;
        const output = this.getIndividualTextOutput();
        if (!boxWidth || !boxHeight || !output.width || !output.height) {
            return 1;
        }
        return Math.max(output.width / boxWidth, output.height / boxHeight);
    }

    clampIndividualTextOutput(field, changed) {
        const output = this.getIndividualTextOutput();
        clampOutputPosition(field.output, output.width, output.height, changed);
    }

    /** Min. font size is not bigger than the font size. */
    clampIndividualTextExplanation() {
        const explanation = this.json.admin.individualText.explanation;
        explanation.fontSize = Math.max(1, Number(explanation.fontSize) || 1);
        explanation.minFontSize = Math.min(Math.max(1, Number(explanation.minFontSize) || 1), explanation.fontSize);
    }

    /**
     * The screen size and the output picture (Scenes, General) change on other tabs, while the Individual Text tab is not shown.
     * Fits its elements right away: its inputs would otherwise empty the values out of their new limits.
     */
    watchIndividualTextLayout() {
        this.$scope.$watchGroup([
            () => this.json && this.getIndividualTextScreenSize().width,
            () => this.json && this.getIndividualTextScreenSize().height
        ], () => this.fitIndividualTextScreen());

        this.$scope.$watchGroup([
            () => this.json && this.getIndividualTextOutput().width,
            () => this.json && this.getIndividualTextOutput().height
        ], (size) => {
            const settings = this.json && this.json.admin.individualText;
            if (!settings || !size[0] || !size[1]) {
                return;
            }
            // Positions are % of the picture and keep their place; px font sizes follow its height,
            // also when the picture changed on another tab or in another session (outputSize is the saved one)
            const saved = settings.outputSize;
            if (saved && saved.height && saved.height !== size[1]) {
                settings.fields.forEach((field) => scaleOutputFont(field.output, size[1] / saved.height));
            }
            settings.outputSize = {width: size[0], height: size[1]};
            settings.fields.forEach((field) => clampOutputPosition(field.output, size[0], size[1]));
            this.fitIndividualTextScreen();
        });
    }

    fitIndividualTextScreen() {
        const settings = this.json && this.json.admin.individualText;
        const screen = this.json && this.getIndividualTextScreenSize();
        if (!settings || !screen || !screen.width || !screen.height) {
            return;
        }
        const ratio = this.getIndividualTextOutputRatio();
        if (ratio) {
            // Same px proportions as the print layout: width is % of the screen width, height % of its height
            settings.outputPreview.width = settings.outputPreview.height * ratio * screen.height / screen.width;
        }
        clampScreenArea(settings.outputPreview, true);
        [settings.explanation.area, settings.doneButton].concat(settings.fields.map((field) => field.area))
            .forEach((area) => clampScreenArea(area));
    }

    /** Keeps the cloud text within the field's allowed input. */
    sanitizeIndividualTextValue(field) {
        field.output.text = sanitizeIndividualText(field.output.text, field.inputType);
    }

    deleteIndividualTextAttachment(owner, key) {
        const attachment = owner[key];
        owner[key] = null;
        this.deleteCompanyAttachment(attachment);
    }

    getDefaultOfflineQrCode() {
        return {
            enabled: false,
            background: null,
            doneButton: {
                width: 6,
                height: 6,
                x: 89,
                y: 92.5,
                customIcon: null
            },
            qrCode: {
                width: 7,
                height: 7,
                x: 50,
                y: 11
            }
        };
    }

    ensureOfflineQrCode() {
        if (!this.json || !this.json.admin) {
            return;
        }
        if (!this.json.admin.offlineQrCode) {
            this.json.admin.offlineQrCode = this.getDefaultOfflineQrCode();
            return;
        }
        const defaults = this.getDefaultOfflineQrCode();
        const offlineQr = this.json.admin.offlineQrCode;
        if (offlineQr.enabled === undefined) {
            offlineQr.enabled = false;
        }
        if (offlineQr.background === undefined) {
            offlineQr.background = null;
        }
        offlineQr.doneButton = this.normalizeOfflineQrArea(offlineQr.doneButton, defaults.doneButton, true);
        offlineQr.qrCode = this.normalizeOfflineQrArea(offlineQr.qrCode, defaults.qrCode, false);
    }

    normalizeOfflineQrArea(area, defaults, keepIcon) {
        const source = area && typeof area === "object" ? area : {};
        source.width = this.normalizeOfflineQrNumber(source.width, defaults.width);
        source.height = this.normalizeOfflineQrNumber(source.height, defaults.height);
        source.x = this.normalizeOfflineQrNumber(source.x, defaults.x);
        source.y = this.normalizeOfflineQrNumber(source.y, defaults.y);
        if (keepIcon) {
            if (source.customIcon === undefined) {
                source.customIcon = null;
            }
        } else {
            delete source.customIcon;
        }
        return source;
    }

    normalizeOfflineQrNumber(value, fallback) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    isOfflineQrLayoutValid(offlineQr) {
        return this.isOfflineQrAreaValid(offlineQr.doneButton) && this.isOfflineQrAreaValid(offlineQr.qrCode);
    }

    isOfflineQrAreaValid(area) {
        return !!(area && area.width && area.height && typeof area.x === "number" && typeof area.y === "number");
    }

    deleteOfflineQrBackground() {
        if (!this.json || !this.json.admin || !this.json.admin.offlineQrCode) {
            return;
        }
        const background = this.json.admin.offlineQrCode.background;
        this.json.admin.offlineQrCode.background = null;
        this.deleteCompanyAttachment(background);
    }

    deleteCompanyAttachment(attachment) {
        if (!this.config || !this.config.company || !attachment) {
            return;
        }
        this.$upload.deleteCompanyAttachment(`${this.config.company}attachments/`, attachment);
    }

    getOfflineQrBackgroundFile() {
        const background = this.json && this.json.admin && this.json.admin.offlineQrCode
            ? this.json.admin.offlineQrCode.background
            : null;
        if (!background || !background.attachment_file || typeof background.attachment_file !== "string") {
            return "";
        }
        return background.attachment_file;
    }

    getOfflineQrBackgroundMode() {
        return this.getOfflineQrBackgroundFile() ? "image" : "color";
    }

    getOfflineQrBackgroundForPreview() {
        const file = this.getOfflineQrBackgroundFile();
        if (!file || this.getFileType({attachment_file: file}) === "mp4") {
            return "[]";
        }
        return JSON.stringify([file]);
    }

    getOfflineQrVideoBackground() {
        const file = this.getOfflineQrBackgroundFile();
        return file && this.getFileType({attachment_file: file}) === "mp4" ? file : "";
    }

    getOfflineQrPreviewAreas() {
        if (!this.json || !this.json.admin || !this.json.admin.offlineQrCode) {
            return {};
        }
        return {
            done: this.json.admin.offlineQrCode.doneButton,
            qrCode: this.json.admin.offlineQrCode.qrCode
        };
    }

    checkIsVideo(name) {
        if (name === undefined || name === null) {
            return false;
        } else {
            return name.indexOf(".mp4") !== -1 || name.indexOf(".webm") !== -1;
        }
    }

    deleteOfflineFormsBackground() {
        this.$upload.delete(this.json.admin.offlineForms.backgroundImage.url).then(() => {
            this.json.admin.offlineForms.backgroundImage = null;
            this.saveDataInConfig();
        });
    }

    setCheckboxesLikeRadiobutton(val) {
        this.json.admin.offlineForms.formCheckboxes.map(el => {
            if (!el.isInfobox) {
                el.typeIsCheckbox = val;
            } else {
                el.typeIsCheckbox = false;
            }
        });
    }

    checkIsActiveCheckboxesOfTypeCheckbox(form) {
        if (form) {
            let arr = form.filter(el => !el.isInfobox && el.active);
            return arr.length <= 1;
        } else {
            return false;
        }
    }

    setGdprData(value) {
        this.json.admin.gdpr.showGdpr = !value;
        this.saveDataInConfig();
    }

    useGdprCustomButton(value) {
        this.json.admin.gdpr.gdprCustomButtonsEnabled = !value;
        this.saveDataInConfig();
    }

    setCustomLogo(data) {
        if (data.customLogoFile == undefined) {
            Object.assign(this.json.admin.ai.customLogo, {customLogoFile: []});
        }
        this.saveDataInConfig();
    }


    isActiveStep(title) {
        return this.currentStep === title;
    }

    stepChanged(step) {
        this.currentStep = step;  // step може відповідати номеру кроку
    }

    checkBoothOptions() {
        const booths = ["photoBooth", "gifBooth", "boomerangBooth", "videoBooth"];
        let enabledBooths = booths.filter(booth => this.json.admin[booth] && this.json.admin[booth].enabled);

        if (enabledBooths.length === 1) {
            const booth = enabledBooths[0];

            if (booth === "videoBooth") {
                return this.json.admin[booth] && this.json.admin[booth].allowSharing === true;
            }

            const allowPrinting = this.json.admin[booth] && this.json.admin[booth].allowPrinting;
            const allowSharing = this.json.admin[booth] && this.json.admin[booth].allowSharing;

            return (allowPrinting === true && allowSharing === false) || (allowPrinting === false && allowSharing === true);
        }

        return false;
    }

    formatTime(milliseconds) {
        var totalSeconds = Math.floor(milliseconds / 1000);
        var minutes = Math.floor(totalSeconds / 60);
        var seconds = totalSeconds % 60;

        var result = "";
        if (minutes > 0) {
            result += minutes + " min ";
        }
        if (seconds > 0 || minutes === 0) {
            result += seconds + " seconds";
        }
        return result.trim();
    }

    updateCaptureTime() {
        this.json.admin.videoBooth.captureTime = this.json.admin.videoBooth.sliderValue * 1000;
    }

    handleFeatureDeactivation(featureName, isActive) {
        if (!this.json || !this.json.admin || !this.json.admin.scenes || !this.json.admin.scenes.scenesList) {
            return;
        }
        if(!isActive){
            this.json.admin.scenes.scenesList.forEach(scene => {
                if (scene.selectedFeature && scene.selectedFeature.value === featureName) {
                    scene.enabled = isActive;
                }
            });
        }
    }
}

export default ConfigurationUpdateController;



// WEBPACK FOOTER //
// ./sources/components/configurations/configuration-update/configuration-update.controller.js